"use client"

import { useState, useContext, useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from "react"
import { useParams, useLocation } from "react-router-dom"
import { ErpContext } from "../../context/ErpContext.js"
import Cookies from "js-cookie"
import { useMemo } from "react"

// Material UI Components
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  Divider,
  Tooltip,
  FormControlLabel,
  InputAdornment,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  alpha,
  Checkbox,
  Collapse,
  Chip,
  Modal,
  Switch,
  Badge,
} from "@mui/material"

// Material UI Icons
import {
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
  PersonAddAlt as PersonAddAltIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  DirectionsBus as DirectionsBusIcon,
  LocationOn as LocationOnIcon,
  Group as GroupIcon,
  Groups as GroupsIcon,
  Luggage as LuggageIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  SettingsBackupRestore as RollbackIcon,
  Calculate as CalculateIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material"


// Components
import CrearEntidadClienteModalComponent from "../adminComponent/CrearEntidadClienteModalComponent.js"
import "./GestionGenerarClientesPaquete.css"
import {
  comparePersonasByApellidoNombre,
  formatPersonaApellidoNombre,
} from "../../utils/personaDisplay.js"

import dayjs from "dayjs"
import 'dayjs/locale/es'
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"

// Styled Table Components
import { styled } from "@mui/material/styles"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell, { tableCellClasses } from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableFooter from "@mui/material/TableFooter"
import TableSortLabel from "@mui/material/TableSortLabel"

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#5b5b5f",
    color: theme.palette.common.white,
    fontWeight: 600,
    padding: "8px 12px",
    "& .MuiTableSortLabel-root": {
      color: theme.palette.common.white,
    },
    "& .MuiTableSortLabel-root:hover": {
      color: theme.palette.common.white,
    },
    "& .MuiTableSortLabel-root.Mui-active": {
      color: theme.palette.common.white,
    },
    "& .MuiTableSortLabel-icon": {
      color: `${theme.palette.common.white} !important`,
    },
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.1),
    transition: "background-color 0.2s ease",
  },
  "& .MuiTableCell-root": {
    padding: "6px 12px",
  },
}))

/**
 * Componente para generar ventas de agencia de viajes
 * 
 * NUEVA LÓGICA:
 * 1. Seleccionar Grupo (item con esServicio=false) y Destino (ubicación)
 * 2. Agregar Pasajeros (entidades) al viaje
 * 3. Generar una transacción por cada pasajero
 * 
 * PROPS OPCIONALES (para modo modal):
 * @param {Object} grupoInicial - Item grupo pre-seleccionado
 * @param {Object} destinoInicial - Destino/ubicación pre-seleccionado
 * @param {Boolean} modoModal - Si está en modo modal (ajusta layout)
 * @param {Function} onClose - Callback para cerrar el modal
 */
const GestionGenerarVentaComponent = forwardRef(({ 
  grupoInicial = null, 
  destinoInicial = null, 
  modoModal = false,
  onClose = null,
  soloLectura = false,
}, ref) => {
  const theme = useTheme()
  const location = useLocation()
  const { id } = useParams()

  const esSoloLectura = Boolean(soloLectura)

  // Función para formatear números en formato argentino
  const formatNumberArgentina = (value) => {
    if (value === null || value === undefined || value === "") return ""
    const num = typeof value === "string" ? parseFloat(value.replace(/\./g, "").replace(",", ".")) : value
    if (isNaN(num)) return ""
    return num.toLocaleString("es-AR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  }

  // Función para parsear números desde formato argentino
  const parseNumberArgentina = (value) => {
    if (!value || value === "") return 0
    const cleaned = value.toString().replace(/\./g, "").replace(",", ".")
    const parsed = parseFloat(cleaned)
    return isNaN(parsed) ? 0 : Math.round(parsed)
  }

  const getCuotasPagadas = (pasajero) => Number(pasajero?.cuotasPagadas || 0)
  const tieneCuotasPagadas = (pasajero) => getCuotasPagadas(pasajero) > 0
  const getViajaEfectivo = (pasajero) => pasajero?.viaja === true

  const normalizarPasajero = (pasajero) => {
    const cuotasPagadas = getCuotasPagadas(pasajero)
    return {
      ...pasajero,
      cuotasPagadas,
      viaja: pasajero?.viaja === true,
      overridePlan: pasajero?.overridePlan ?? false,
      planAplicado: pasajero?.planAplicado ?? false,
    }
  }

  const calcularPrecioNetoPasajero = (pasajero) => {
    if (!pasajero) return 0
    const descuento = pasajero.liberado === true ? 100 : (pasajero.descuento || 0)
    const base = pasajero.precio || 0
    const neto = base * (1 - descuento / 100)
    return Math.round(neto)
  }

  const toIsoDate = (value) => {
    if (!value) return null
    const parsed = dayjs(value)
    if (!parsed.isValid()) return null
    return parsed.format("YYYY-MM-DD")
  }

  const toDateOnly = (value) => {
    if (!value) return null
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
      if (value.includes('T')) return value.split('T')[0]
    }
    const d = dayjs(value)
    return d.isValid() ? d.format('YYYY-MM-DD') : null
  }

  // Visualización se maneja por DatePicker con format

  const primeraDelMesSiguiente = (base = dayjs()) => {
    const d = dayjs(base)
    return d.date(1).add(1, 'month')
  }

  const ajustarAlLunesSiFinde = (date) => {
    let d = dayjs(date)
    const dow = d.day() // 0=Dom, 6=Sab
    if (dow === 6) d = d.add(2, 'day')
    if (dow === 0) d = d.add(1, 'day')
    return d
  }

  // Función auxiliar para redondear según la longitud del valor (fórmula Excel)
  const redondearSegunLongitud = (valor) => {
    if (valor === 0) return 0
    const len = Math.abs(valor).toString().replace(/\D/g, '').length // Solo dígitos
    if (len <= 4) return Math.floor(valor / 100) * 100        // Redondear a centenas
    if (len === 5) return Math.floor(valor / 1000) * 1000     // Redondear a miles
    if (len === 6) return Math.floor(valor / 10000) * 10000   // Redondear a decenas de mil
    if (len === 7) return Math.floor(valor / 100000) * 100000 // Redondear a centenas de mil
    // Para valores muy grandes (8+ dígitos), redondear a millones
    return Math.floor(valor / 1000000) * 1000000
  }

  // Función de distribución de capital con ajuste en primera cuota (fórmula Excel)
  const distribuirCapitalConAjustePrimera = (cantidad, total) => {
    if (cantidad <= 0) return []
    if (cantidad === 1) return [total]

    // Calcular capital base redondeado (fórmula Excel: ROUND(H4/H5;0))
    const capitalPromedio = Math.round(total / cantidad)
    
    // Aplicar redondeo según longitud (fórmula Excel FLOOR con longitud)
    const capitalRedondeado = redondearSegunLongitud(capitalPromedio)
    
    // Calcular suma de capitales de cuotas 2 a N
    const sumaRestantes = capitalRedondeado * (cantidad - 1)
    
    // Primera cuota = residual (fórmula Excel: =H4-SUM(K13:K18))
    const primeraCuota = total - sumaRestantes
    
    // Construir array: [primeraCuota, capitalRedondeado, capitalRedondeado, ...]
    const valores = [primeraCuota]
    for (let i = 1; i < cantidad; i++) {
      valores.push(capitalRedondeado)
    }
    
    return valores
  }

  // Mantener distribuirResto original para casos donde se necesite distribución uniforme
  const distribuirResto = (cantidad, total) => {
    const base = Math.floor(total / cantidad)
    let resto = total - base * cantidad
    const valores = []
    for (let i = 0; i < cantidad; i++) {
      let valor = base
      if (resto > 0) {
        valor += 1
        resto -= 1
      }
      valores.push(valor)
    }
    return valores
  }

  const obtenerOffsetMesInicioFechasFijasGlobal = (fechaBase = null) => {
    const base = fechaBase ? dayjs(fechaBase) : dayjs()
    if (!base.isValid()) return 1
    return base.date() < 20 ? 0 : 1
  }

  // ⭐ NUEVA FUNCIÓN: Generar fechas fijas (día 20 y día 30/28)
  const generarFechasVencimientoFijas = (
    numeroCuota,
    fechaBase = null,
    mesInicioOffset = 1
  ) => {
    // Usar fecha base o fecha actual
    const base = fechaBase ? dayjs(fechaBase) : dayjs()
    
    const offsetMesNormalizado = Number.isFinite(Number(mesInicioOffset))
      ? Math.max(0, Math.round(Number(mesInicioOffset)))
      : 1
    
    // Calcular mes de vencimiento tomando un offset inicial configurable
    // offset 1 = comportamiento histórico (arranca mes siguiente)
    // offset 0 = arranca mes actual (si hoy < 20 en recálculo global)
    const mesVencimiento = base.add(offsetMesNormalizado + (numeroCuota - 1), 'month')
    
    // 1er vencimiento: día 20 del mes
    const primerVto = mesVencimiento.date(20)
    
    // 2do vencimiento: día 30 (o 28 si es febrero)
    const mes = mesVencimiento.month() // 0-indexed: 0=enero, 1=febrero, etc.
    const esFebrero = mes === 1
    const diaSegundoVto = esFebrero ? 28 : 30
    const segundoVto = mesVencimiento.date(diaSegundoVto)
    
    return {
      fechaPrimerVto: toIsoDate(primerVto),
      fechaSegundoVto: toIsoDate(segundoVto)
    }
  }

  const generarPlanDetalles = (
    header,
    detallesPrevios = [],
    usarFechasFijas = true,
    configFechasFijas = {}
  ) => {
    const cantidadCuotas = Number(header?.cantidadCuotas || 0)
    if (cantidadCuotas <= 0) return []

    const precioBase = Math.round(Number(header?.precioBaseReferencia || 0))
    const porcentajeInteres = Number(header?.porcentajeInteres || 0) / 100 // Convertir % a decimal
    const porcentajeDescuento = Number(header?.porcentajeDescuento || 0) / 100 // Convertir % a decimal
    const offsetMesInicialFechasFijas = Number.isFinite(Number(configFechasFijas?.offsetMesInicial))
      ? Math.max(0, Math.round(Number(configFechasFijas.offsetMesInicial)))
      : 1
    
    // ⭐ CASO ESPECIAL: 1 cuota = pago único sin interés
    if (cantidadCuotas === 1) {
      const fechas = usarFechasFijas 
        ? generarFechasVencimientoFijas(1, null, offsetMesInicialFechasFijas)
        : {
            fechaPrimerVto: toIsoDate(primeraDelMesSiguiente(dayjs())),
            fechaSegundoVto: toIsoDate(ajustarAlLunesSiFinde(primeraDelMesSiguiente(dayjs()).add(14, 'day')))
          }
      
      const montoDescuento = Math.min(
        precioBase,
        Math.max(0, Math.round(precioBase * porcentajeDescuento))
      )
      const montoNeto = Math.max(0, precioBase - montoDescuento)

      return [{
        cuotaNro: 1,
        fechaPrimerVto: fechas.fechaPrimerVto,
        fechaSegundoVto: fechas.fechaSegundoVto,
        montoBase: precioBase,
        montoDescuento,
        montoInteres: 0, // Sin interés en pago único
        montoTotal: montoNeto,
        descripcion: null,
        redondeoManual: null,
      }]
    }
    
    // Calcular cuotas que tendrán interés
    const indicesInteres = []
    for (let i = 1; i <= cantidadCuotas; i++) {
      const aplicaInteres = header?.interesPrimeraUltima
        ? true
        : i !== 1 && i !== cantidadCuotas // Sin interés en primera y última
      if (aplicaInteres) indicesInteres.push(i)
    }

    // Distribuir capital con ajuste en primera cuota (nueva fórmula Excel)
    const baseDistribucion = distribuirCapitalConAjustePrimera(cantidadCuotas, precioBase)

    const prevMap = new Map(
      detallesPrevios.map((det) => [det.cuotaNro, det])
    )

    const detalles = []
    const startFirstVto = primeraDelMesSiguiente(dayjs())
    
    for (let i = 1; i <= cantidadCuotas; i++) {
      const previo = prevMap.get(i) || {}
      // ⭐ Preservar cuotas pagadas (idTransaccion) - no regenerar
      if (previo.idTransaccion != null && previo.idTransaccion !== undefined) {
        detalles.push({
          cuotaNro: i,
          fechaPrimerVto: previo.fechaPrimerVto ?? null,
          fechaSegundoVto: previo.fechaSegundoVto ?? null,
          montoBase: previo.montoBase ?? 0,
          montoDescuento: previo.montoDescuento ?? 0,
          montoInteres: previo.montoInteres ?? 0,
          montoTotal: previo.montoTotal ?? (previo.montoBase ?? 0) + (previo.montoInteres ?? 0) - (previo.montoDescuento ?? 0),
          descripcion: previo.descripcion ?? null,
          redondeoManual: previo.redondeoManual ?? null,
          idTransaccion: previo.idTransaccion,
        })
        continue
      }
      const montoBase = baseDistribucion[i - 1] ?? 0
      const montoDescuento = Math.min(
        montoBase,
        Math.max(0, Math.round(montoBase * porcentajeDescuento))
      )
      const montoBaseNeto = Math.max(0, montoBase - montoDescuento)
      
      // Calcular interés sobre el monto de la cuota individual (nuevo cálculo)
      // El interés se aplica como porcentaje del monto base de cada cuota
      const montoInteres = indicesInteres.includes(i) 
        ? Math.round(montoBaseNeto * porcentajeInteres) 
        : 0
      
      // Total = (base - descuento) + interés
      const montoTotal = montoBaseNeto + montoInteres

      // ⭐ Generar fechas según el modo seleccionado
      let fechas
      if (usarFechasFijas) {
        fechas = generarFechasVencimientoFijas(i, null, offsetMesInicialFechasFijas)
      } else {
        // Fechas auto antiguas: 1er vto = primer día de mes siguiente + (i-1) meses
        const vto1Auto = startFirstVto.add(i - 1, 'month')
        // 2do vto = 15 días después; si cae finde, mover a lunes
        const vto2Auto = ajustarAlLunesSiFinde(vto1Auto.add(14, 'day'))
        fechas = {
          fechaPrimerVto: toIsoDate(vto1Auto),
          fechaSegundoVto: toIsoDate(vto2Auto)
        }
      }

      detalles.push({
        cuotaNro: i,
        // ⭐ Si usarFechasFijas = true, SIEMPRE usar las fechas generadas (ignora las previas)
        // Si usarFechasFijas = false, usar las previas si existen (comportamiento anterior)
        fechaPrimerVto: usarFechasFijas ? fechas.fechaPrimerVto : (previo.fechaPrimerVto || fechas.fechaPrimerVto),
        fechaSegundoVto: usarFechasFijas ? fechas.fechaSegundoVto : (previo.fechaSegundoVto || fechas.fechaSegundoVto),
        montoBase,
        montoDescuento,
        montoInteres,
        montoTotal,
        descripcion: previo.descripcion || null,
        redondeoManual: previo.redondeoManual || null,
      })
    }

    return detalles
  }

  const calcularTotalesPlanLocal = (detalles = []) => {
    return detalles.reduce(
      (acc, det) => {
        acc.base += det.montoBase ?? 0
        acc.descuento += det.montoDescuento ?? 0
        acc.interes += det.montoInteres ?? 0
        acc.total += det.montoTotal ?? ((det.montoBase ?? 0) + (det.montoInteres ?? 0) - (det.montoDescuento ?? 0))
        return acc
      },
      { base: 0, descuento: 0, interes: 0, total: 0 }
    )
  }

  const ajustarCampoHastaObjetivo = (lista, campo, objetivo) => {
    const sumaActual = lista.reduce((acc, det) => acc + (det[campo] ?? 0), 0)
    const diferencia = objetivo - sumaActual
    if (diferencia !== 0) {
      const ultimaCuota = [...lista]
        .reverse()
        .find((det) => (det[campo] ?? 0) >= 0)
      if (ultimaCuota) {
        ultimaCuota[campo] = (ultimaCuota[campo] ?? 0) + diferencia
      }
    }
  }

  const escalarPlanParaPasajero = (detalles, factor, totalesPlan, totalObjetivo) => {
    const escalados = detalles.map((detalle) => ({
      cuotaNro: detalle.cuotaNro,
      // Normalizar a date-only para evitar desfases por timezone
      fechaPrimerVto: toDateOnly(detalle.fechaPrimerVto),
      fechaSegundoVto: toDateOnly(detalle.fechaSegundoVto),
      montoBase: Math.round((detalle.montoBase ?? 0) * factor),
      montoDescuento: Math.round((detalle.montoDescuento ?? 0) * factor),
      montoInteres: Math.round((detalle.montoInteres ?? 0) * factor),
      montoTotal: Math.round((detalle.montoTotal ?? ((detalle.montoBase ?? 0) + (detalle.montoInteres ?? 0) - (detalle.montoDescuento ?? 0))) * factor),
      descripcion: detalle.descripcion || null,
      redondeoManual: detalle.redondeoManual || null,
    }))

    ajustarCampoHastaObjetivo(escalados, "montoBase", Math.round((totalesPlan.base ?? 0) * factor))
    ajustarCampoHastaObjetivo(escalados, "montoDescuento", Math.round((totalesPlan.descuento ?? 0) * factor))
    ajustarCampoHastaObjetivo(escalados, "montoInteres", Math.round((totalesPlan.interes ?? 0) * factor))
    escalados.forEach((det) => {
      const base = Math.max(0, Math.round(Number(det.montoBase || 0)))
      const descuento = Math.min(Math.max(0, Math.round(Number(det.montoDescuento || 0))), base)
      const interes = Math.max(0, Math.round(Number(det.montoInteres || 0)))
      det.montoBase = base
      det.montoDescuento = descuento
      det.montoInteres = interes
      det.montoTotal = Math.max(0, base - descuento) + interes
    })

    const objetivoTotal = totalObjetivo ?? Math.round((totalesPlan.total ?? 0) * factor)
    const totalActual = escalados.reduce((acc, det) => acc + (det.montoTotal ?? 0), 0)
    const diferencia = objetivoTotal - totalActual
    if (diferencia !== 0) {
      const ultima = [...escalados].reverse().find((det) => Number.isFinite(det.montoTotal))
      if (ultima) {
        ultima.montoBase = Math.max(0, (ultima.montoBase || 0) + diferencia)
        ultima.montoTotal = Math.max(0, (ultima.montoBase || 0) - (ultima.montoDescuento || 0)) + (ultima.montoInteres || 0)
      }
    }

    return escalados
  }

  // Context
  const {
    getItemTresLetras,
    itemTresLetras,
    arrayItemTresLetras,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
    postTransaccionItem,
    postTransaccion,
    putTransaccion,
    updateDeleteTransaccion,
    getUsuario,
    usuarios,
    getUbicacion,
    ubicaciones,
    isNavbarCollapsed,
    getItemAtributoNoEliminados,
    itemAtributoNoEliminados,
    getItemUbicacionFilter,
    getClientesPorGrupoYDestino,
    updateTransaccionItem,
    postCuentaCorrienteCuotasDetalle,
    getCuentaCorrienteCuotas,
    getPlanCuotas,
    postPlanCuotas,
    aplicarPlanCuotas,
    getRecetasViajes,
    getEntidadByTipoEntidad,
    crearRecetaApi,
  } = useContext(ErpContext)

  // Estados principales
  const [selectedGrupo, setSelectedGrupo] = useState(null) // Item grupo seleccionado
  const [selectedDestino, setSelectedDestino] = useState(null) // Destino/ubicación seleccionado
  const [selectedPasajero, setSelectedPasajero] = useState(null) // Pasajero a agregar
  const [searchTextGrupo, setSearchTextGrupo] = useState("")
  const [searchTextPasajero, setSearchTextPasajero] = useState("")
  const [pasajerosList, setPasajerosList] = useState([]) // Lista de pasajeros agregados
  const [valoresOriginalesPasajeros, setValoresOriginalesPasajeros] = useState({}) // Valores originales para comparar cambios
  const [sortConfig, setSortConfig] = useState({ field: "apellido", direction: "asc" }) // Estado para sorting client-side
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null) // Usuario logueado (se carga automáticamente)
  const [ubicacionesFiltradas, setUbicacionesFiltradas] = useState([]) // Ubicaciones del grupo seleccionado
  const [precioPaqueteActual, setPrecioPaqueteActual] = useState(0)
  const [idPaqueteActual, setIdPaqueteActual] = useState(null)
  const [fechaInicioViaje, setFechaInicioViaje] = useState(null)
  const [fechaFinViaje, setFechaFinViaje] = useState(null)
  const [precioPaqueteLoading, setPrecioPaqueteLoading] = useState(false)
  const precioPaqueteFetchRef = useRef({ token: 0, key: null })
  const getRecetasViajesRef = useRef(getRecetasViajes)
  useEffect(() => { getRecetasViajesRef.current = getRecetasViajes })
  const didFormatGrupoRef = useRef(false)

    // Total de butacas (inventario) para el grupo/destino seleccionado
    const totalButacas = useMemo(() => {
      if (!selectedDestino) return 0
      const match = ubicacionesFiltradas.find(
        (it) => Number(it.idUbicacion) === Number(selectedDestino.id)
      )
      const inventario = match?.inventario
      const parsed = Number(inventario)
      return Number.isFinite(parsed) ? parsed : 0
    }, [ubicacionesFiltradas, selectedDestino])
  
    const totalPasajeros = pasajerosList.filter((p) => getViajaEfectivo(p)).length
    const totalEntidadesAgregadas = pasajerosList.length
    const totalConfirmados = pasajerosList.filter((p) => getViajaEfectivo(p)).length
    const capacidadPorcentaje = useMemo(() => {
      if (!totalButacas || totalButacas <= 0) return 0
      return (totalEntidadesAgregadas / totalButacas) * 100
    }, [pasajerosList.length, totalButacas])

  // Estados para descuentos
  const [aplicarDescuento, setAplicarDescuento] = useState(false)
  const [tempValorDescuento, setTempValorDescuento] = useState("0")
  const [tempPorcentajeDescuento, setTempPorcentajeDescuento] = useState("0")

  // Estados para totales
  const [precioBaseTotal, setPrecioBaseTotal] = useState(0)
  const [precioTotal, setPrecioTotal] = useState(0)
  const [montoTotalDescuentos, setMontoTotalDescuentos] = useState(0)

  // Estados para modales
  const [abrirModalCrearPasajero, setAbrirModalCrearPasajero] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [pasajeroAEditar, setPasajeroAEditar] = useState(null)
  const [abrirModalCuotasMasivo, setAbrirModalCuotasMasivo] = useState(false)
  const [planCuotasState, setPlanCuotasState] = useState({
    open: false,
    loading: false,
    header: null,
    detalles: [],
    planId: null,
    mode: "global", // global | override
    targetPasajero: null,
  })
  const [planTotales, setPlanTotales] = useState({ base: 0, descuento: 0, interes: 0, total: 0 })
  const [planGuardando, setPlanGuardando] = useState(false)
  const [planError, setPlanError] = useState(null)
  
  // ⭐ NUEVO: Estados para recálculo individual en modo override
  const [overrideConfig, setOverrideConfig] = useState({
    cantidadCuotas: 7,
    porcentajeInteres: 10,
    porcentajeDescuento: 0,
    interesPrimeraUltima: false,
  })
  const planPrecioBaseRefInicial = useRef(null)

  const actualizarPlanDetalles = (detalles) => {
    setPlanCuotasState((prev) => ({
      ...prev,
      detalles,
    }))
    setPlanTotales(calcularTotalesPlanLocal(detalles))
  }

  // Estados para UI
  const [loading, setLoading] = useState(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  })
  // Evita contar cambios durante la carga inicial del snapshot base
  const [snapshotReady, setSnapshotReady] = useState(false)

  // Número de cambios detectados (existentes modificados + nuevos)
  const TOLERANCIA_PRECIO = 0.1 // Evita falsos positivos por redondeo

  const pasajeroTieneCambios = (p) => {
    if (!snapshotReady) return false
    // Nuevos pasajeros cuentan como cambio
    const idTx = p.idTransaccion ?? p.id
    if (!idTx) return true
    const originales = valoresOriginalesPasajeros[idTx]
    if (!originales) return true

    // Recalcular precio final actual con descuentos y descuentos de transacción proporcionales
    const valorDescTransaccion = Number.parseFloat(tempValorDescuento) || 0
    const porcentajeDescTransaccion = Number.parseFloat(tempPorcentajeDescuento) || 0

    // Descuento efectivo: si liberado => 100%, sin importar lo que diga porcentajeDescuento
    const descuentoActual = (p.liberado === true) ? 100 : (p.descuento || 0)
    const descuentoOriginalEfectivo = (originales.liberado === true) ? 100 : (originales.descuento || 0)

    const precioConDescuentoIndividual = p.precio * (1 - descuentoActual / 100)
    const totalBase = pasajerosList.reduce((t, px) => t + (px.precio * (1 - (px.descuento || 0) / 100)), 0)
    const proporcion = totalBase > 0 ? precioConDescuentoIndividual / totalBase : 0
    const descFijo = valorDescTransaccion * proporcion
    const descPorc = precioConDescuentoIndividual * (porcentajeDescTransaccion / 100)
    let precioFinal = precioConDescuentoIndividual - descFijo - descPorc
    if (precioFinal < 0) precioFinal = 0

    const distintos = (
      originales.viaja !== getViajaEfectivo(p) ||
      originales.liberado !== (p.liberado === true) ||
      originales.cuotas !== (p.cuotas ?? null) ||
      descuentoOriginalEfectivo !== descuentoActual ||
      Math.abs((originales.precio || 0) - precioFinal) > TOLERANCIA_PRECIO
    )
    return distintos
  }

  const difPasajero = (p) => {
    const idTx = p.idTransaccion ?? p.id
    if (!idTx) return ["nuevo"]
    const originales = valoresOriginalesPasajeros[idTx]
    if (!originales) return ["nuevo"]

    const valorDescTransaccion = Number.parseFloat(tempValorDescuento) || 0
    const porcentajeDescTransaccion = Number.parseFloat(tempPorcentajeDescuento) || 0

    const descuentoActual = (p.liberado === true) ? 100 : (p.descuento || 0)
    const descuentoOriginalEfectivo = (originales.liberado === true) ? 100 : (originales.descuento || 0)

    const precioConDescuentoIndividual = p.precio * (1 - descuentoActual / 100)
    const totalBase = pasajerosList.reduce((t, px) => t + (px.precio * (1 - (px.descuento || 0) / 100)), 0)
    const proporcion = totalBase > 0 ? precioConDescuentoIndividual / totalBase : 0
    const descFijo = valorDescTransaccion * proporcion
    const descPorc = precioConDescuentoIndividual * (porcentajeDescTransaccion / 100)
    let precioFinal = precioConDescuentoIndividual - descFijo - descPorc
    if (precioFinal < 0) precioFinal = 0

    const diffs = []
    if (originales.viaja !== getViajaEfectivo(p)) diffs.push("viaja")
    if (originales.liberado !== (p.liberado === true)) diffs.push("liberado")
    if (originales.cuotas !== (p.cuotas ?? null)) diffs.push("cuotas")
    if (descuentoOriginalEfectivo !== descuentoActual) diffs.push("descuento")
    if (Math.abs((originales.precio || 0) - precioFinal) > TOLERANCIA_PRECIO) diffs.push("total")
    return diffs
  }

  const cambiosPendientes = useMemo(() => {
    if (!snapshotReady) return 0
    if (!pasajerosList || pasajerosList.length === 0) return 0

    const valorDescTransaccion = Number.parseFloat(tempValorDescuento) || 0
    const porcentajeDescTransaccion = Number.parseFloat(tempPorcentajeDescuento) || 0

    let count = 0
    for (const p of pasajerosList) {
      // Contar pasajeros marcados para eliminar o con cambios
      if (p.marcadoParaEliminar || pasajeroTieneCambios(p)) count++
    }

    return count
  }, [pasajerosList, valoresOriginalesPasajeros, tempValorDescuento, tempPorcentajeDescuento])

  // Revertir cambios: vuelve a los valores originales y elimina pasajeros nuevos
  const handleRevertirCambios = () => {
    if (!pasajerosList || pasajerosList.length === 0) return

    const revertidos = pasajerosList
      .filter((p) => !!p.idTransaccion)
      .map((p) => {
        const orig = valoresOriginalesPasajeros[p.idTransaccion]
        if (!orig) return p
        return {
          ...p,
          viaja: orig.viaja,
          liberado: orig.liberado,
          cuotas: orig.cuotas,
          descuento: orig.descuento,
          // precio base del pasajero se mantiene; el total se recalcula en render
        }
      })

    setPasajerosList(revertidos.map((p) => normalizarPasajero(p)))
    showSnackbar("Cambios revertidos", "info")
  }

  // Referencias
  const autocompleteGrupoRef = useRef(null)
  const autocompletePasajeroRef = useRef(null)

  // Función para mostrar snackbar
  const showSnackbar = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    })
  }

  // Función para cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    })
  }

  const validarCapacidadDisponible = () => {
    if (!Number.isFinite(totalButacas) || totalButacas <= 0) {
      showSnackbar(
        "Este viaje no tiene butacas disponibles (0). Aumentá las butacas para poder crear/agregar pasajeros.",
        "warning"
      )
      return false
    }

    if (totalButacas > 0 && pasajerosList.length >= totalButacas) {
      showSnackbar(
        `No hay más lugar en este viaje (${pasajerosList.length}/${totalButacas}). Aumentá las butacas para poder crear/agregar pasajeros.`,
        "warning"
      )
      return false
    }
    return true
  }

  // Exponer funciones al componente padre mediante ref
  useImperativeHandle(ref, () => ({
    hayCambiosPendientes: () => {
      if (esSoloLectura) return false
      return cambiosPendientes > 0
    },
    guardarCambios: async () => {
      if (esSoloLectura) return
      // Ejecutar la función de generar/actualizar transacciones
      const fakeEvent = { preventDefault: () => {} }
      await handleGenerarTransacciones(fakeEvent)
    }
  }))

  // Efecto para cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        await getUbicacion()
        await getUsuario()
        await getItemAtributoNoEliminados()
        
        // Cargar itemTresLetras si tenemos grupoInicial para buscar el item completo
        if (grupoInicial) {
          const grupoTexto = grupoInicial.codigo || grupoInicial.descripcion || ""
          if (grupoTexto.length >= 3) {
            await getItemTresLetras(grupoTexto.substring(0, 3))
          }
        }
        
        if (!modoModal) {
          showSnackbar("Datos cargados correctamente", "success")
        }
      } catch (error) {
        console.error("Error al cargar los datos:", error)
        showSnackbar("Error al cargar los datos iniciales", "error")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [grupoInicial])

  // Efecto separado para formatear y setear el grupoInicial cuando itemTresLetras y itemAtributoNoEliminados estén disponibles
  // NOTA: NO se llama getItemTresLetras aquí — lo hace el efecto de inicialización (línea ~709).
  //       Llamarla aquí causaría un bucle: itemTresLetras cambia → efecto dispara → llama fetch → itemTresLetras cambia → ...
  useEffect(() => {
    if (destinoInicial) {
      setSelectedDestino(destinoInicial)
    }

    if (didFormatGrupoRef.current) return
    if (!grupoInicial || !itemTresLetras?.length || !itemAtributoNoEliminados) return

    let itemCompleto = itemTresLetras.find(it => it.id === grupoInicial.id || it.id === grupoInicial.idItem)

    if (!itemCompleto) {
      itemCompleto = itemTresLetras.find(it =>
        it.codigo === grupoInicial.codigo ||
        it.descripcion === grupoInicial.descripcion
      )
    }

    if (itemCompleto) {
      didFormatGrupoRef.current = true
      const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)
      const attributeValues = activeAttributes
        .map((attr, index) => {
          const attributeValue = itemCompleto[`itemDatoAtributo${index + 1}`]
          return attributeValue ? attributeValue : ""
        })
        .filter((val) => val.trim() !== "")
        .join(" ")

      const grupoFormateado = {
        label: `${itemCompleto.codigo} - ${itemCompleto.descripcion}${attributeValues ? " - " + attributeValues : ""}`,
        descripcion: itemCompleto.descripcion,
        codigo: itemCompleto.codigo,
        id: itemCompleto.id,
        atributos: attributeValues,
      }

      setSelectedGrupo(grupoFormateado)
      setSearchTextGrupo(grupoFormateado.label)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getItemTresLetras removido intencionalmente para evitar bucle
  }, [grupoInicial, destinoInicial, itemTresLetras, itemAtributoNoEliminados])

  // Efecto para cargar ubicaciones filtradas cuando se selecciona un grupo
  useEffect(() => {
    const cargarUbicacionesFiltradas = async () => {
      if (selectedGrupo?.id) {
        console.log(`📍 Cargando ubicaciones para grupo ${selectedGrupo.id}`)
        try {
          const itemsUbic = await getItemUbicacionFilter(selectedGrupo.id)
          setUbicacionesFiltradas(itemsUbic || [])
        } catch (error) {
          console.error("Error al cargar ubicaciones filtradas:", error)
          setUbicacionesFiltradas([])
        }
      } else {
        setUbicacionesFiltradas([])
      }
    }
    
    cargarUbicacionesFiltradas()
  }, [selectedGrupo?.id])

  const refrescarPrecioPaquete = useCallback(
    async (idItem = selectedGrupo?.id, idUbicacion = selectedDestino?.id) => {
      if (!idItem || !idUbicacion) {
        setPrecioPaqueteActual(0)
        setIdPaqueteActual(null)
        return 0
      }

      const key = `${idItem}-${idUbicacion}`
      const token = precioPaqueteFetchRef.current.token + 1
      precioPaqueteFetchRef.current = { token, key }
      setPrecioPaqueteLoading(true)

      try {
        const respuesta = await getRecetasViajesRef.current(
          null,
          null,
          idItem,
          null,
          idUbicacion,
          1,
          1
        )
        const paquete = respuesta?.data?.[0]
        const precio = Number(paquete?.monto ?? paquete?.precioPaquete ?? 0)
        const precioFinal = Number.isFinite(precio) ? precio : 0
        if (precioPaqueteFetchRef.current.token === token) {
        console.log("respuesta", respuesta)
          setPrecioPaqueteActual(precioFinal)
          setIdPaqueteActual(paquete?.idPaquete ?? paquete?.id ?? null)
        console.log("precio", precio)
          setFechaInicioViaje(paquete?.fechaInicio ?? null)
          console.log("precioPaqueteActual", precioFinal)
          setFechaFinViaje(paquete?.fechaFin ?? null)
        }
        return precioFinal
      } catch (error) {
        console.error("Error al cargar precio del paquete:", error)
        if (precioPaqueteFetchRef.current.token === token) {
          setPrecioPaqueteActual(0)
          setIdPaqueteActual(null)
          setFechaInicioViaje(null)
          setFechaFinViaje(null)
        }
        return 0
      } finally {
        if (precioPaqueteFetchRef.current.token === token) {
          setPrecioPaqueteLoading(false)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getRecetasViajes via ref estable; solo re-crear cuando cambian grupo/destino
    [selectedGrupo?.id, selectedDestino?.id]
  )

  // Cargar precio del paquete desde Paquete.monto (fuente de verdad)
  useEffect(() => {
    refrescarPrecioPaquete()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo re-ejecutar cuando cambian los IDs primitivos
  }, [selectedGrupo?.id, selectedDestino?.id])

  // ========================================
  // 🔄 Handler para sorting client-side
  // ========================================
  const handleSort = (field) => {
    let direction = 'asc'
    
    // Toggle direction si ya está ordenado por este campo
    if (sortConfig.field === field && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    
    setSortConfig({ field, direction })
  }

  // 🔧 FUNCIÓN: Cargar pasajeros existentes (movida fuera del useEffect para reutilización)
  const cargarPasajerosExistentes = async () => {
    if (selectedGrupo?.id && selectedDestino?.id) {
      console.log(`👥 Cargando pasajeros existentes para grupo ${selectedGrupo.id} y destino ${selectedDestino.id}`)
      try {
        setSnapshotReady(false)
        const pasajerosExistentes = await getClientesPorGrupoYDestino(
          selectedGrupo.id,
          selectedDestino.id
        )
        
        if (pasajerosExistentes && pasajerosExistentes.length > 0) {
          console.log(`✅ ${pasajerosExistentes.length} pasajero(s) existente(s) encontrado(s)`)
          // Normalizar datos para evitar falsos positivos en detección de cambios
          const normalizados = pasajerosExistentes.map((p) => ({
            ...p,
            idTransaccion: p.idTransaccion ?? p.id, // asegurar idTransaccion
            viaja: p.viaja === true,
            cuotasPagadas: Number(p.cuotasPagadas || 0),
            liberado: p.liberado === true, // default false
            cuotas: p.cuotas ?? null, // undefined -> null para comparación estable
            // Usar porcentajeDescuento de backend si está, luego descuento, luego inferir por liberado
            descuento: (p.porcentajeDescuento !== undefined && p.porcentajeDescuento !== null)
              ? Number(p.porcentajeDescuento)
              : ((p.descuento !== undefined && p.descuento !== null)
                  ? Number(p.descuento)
                  : (p.liberado === true ? 100 : 0)),
            precio: Number(p.precio || 0),
            montoTotal: Number(p.montoTotal || 0),
          }))
          setPasajerosList(normalizados.map((p) => normalizarPasajero(p)))
          // Guardar valores originales para detectar cambios
          const valoresOriginales = {}
          normalizados.forEach(p => {
            const idTx = p.idTransaccion ?? p.id
            if (idTx) {
              // Usar montoTotal de la transacción como precio original (0 es válido); fallback a precioOriginal y luego precio base
              const precioOriginal = Number(
                (p.montoTotal ?? p.precioOriginal ?? p.precio ?? 0)
              )
              // Usar el mismo descuento que ya normalizamos (respeta el de la base)
              const descuentoOriginal = Number(p.descuento ?? (p.liberado === true ? 100 : 0))
              valoresOriginales[idTx] = {
                viaja: getViajaEfectivo(p),
                liberado: p.liberado === true,
                cuotas: p.cuotas ?? null,
                descuento: descuentoOriginal,
                precio: precioOriginal,
              }
              
              // LOG DETALLADO para debug
              console.log(`🔍 DEBUG Pasajero: ${p.nombre} ${p.apellido} (tx=${idTx})`)
              console.log(`   Raw data from backend:`, {
                liberado: p.liberado,
                descuento: p.descuento,
                porcentajeDescuento: p.porcentajeDescuento,
                montoTotal: p.montoTotal,
                precio: p.precio,
                viaja: p.viaja,
                cuotas: p.cuotas,
              })
              console.log(`   Valores originales guardados:`, valoresOriginales[idTx])
            }
          })
          setValoresOriginalesPasajeros(valoresOriginales)
          setSnapshotReady(true)
        } else {
          console.log("ℹ️ No hay pasajeros existentes para este grupo y destino")
          setPasajerosList([])
          setValoresOriginalesPasajeros({})
          setSnapshotReady(true)
        }
      } catch (error) {
        console.error("Error al cargar pasajeros existentes:", error)
        setPasajerosList([])
        setValoresOriginalesPasajeros({})
        setSnapshotReady(true)
      }
    } else {
      // Si no hay grupo o destino seleccionado, limpiar la lista
      setPasajerosList([])
      setValoresOriginalesPasajeros({})
      setSnapshotReady(false)
    }
  }

  // Efecto para cargar pasajeros existentes cuando se seleccionan grupo y destino
  useEffect(() => {
    cargarPasajerosExistentes()
  }, [selectedGrupo?.id, selectedDestino?.id])
  
  // ========================================
  // 🔄 useMemo para aplicar sorting client-side
  // ========================================
  const pasajerosOrdenados = useMemo(() => {
    if (!sortConfig.field) {
      return pasajerosList
    }
    
    // Crear copia del array para no mutar el original
    const sorted = [...pasajerosList].sort((a, b) => {
      let valueA, valueB
      
      switch (sortConfig.field) {
        case "apellido":
          return sortConfig.direction === "asc"
            ? comparePersonasByApellidoNombre(a, b)
            : comparePersonasByApellidoNombre(b, a)
        
        case 'dniCuitCuil':
          // Ordenar DNI/CUIT como string con ordenamiento numérico
          valueA = (a.dniCuitCuil || '').toString()
          valueB = (b.dniCuitCuil || '').toString()
          return sortConfig.direction === 'asc'
            ? valueA.localeCompare(valueB, 'es', { numeric: true })
            : valueB.localeCompare(valueA, 'es', { numeric: true })
        
        case 'precio':
          // Ordenar por precio base
          valueA = a.precio || 0
          valueB = b.precio || 0
          return sortConfig.direction === 'asc' 
            ? valueA - valueB 
            : valueB - valueA
        
        case 'total':
          // Ordenar por total calculado (precio con descuentos)
          const precioA = a.precio || 0
          const precioB = b.precio || 0
          valueA = a.liberado ? 0 : precioA * (1 - (a.descuento || 0) / 100)
          valueB = b.liberado ? 0 : precioB * (1 - (b.descuento || 0) / 100)
          return sortConfig.direction === 'asc' 
            ? valueA - valueB 
            : valueB - valueA
        
        default:
          return 0
      }
    })
    
    return sorted
  }, [pasajerosList, sortConfig])

  // Efecto para cargar usuario por defecto
  useEffect(() => {
    const idUsuarioGuardado = Cookies.get("idUsuario")
    if (idUsuarioGuardado && usuarios.length > 0) {
      const usuarioDefault = usuarios.find((user) => user.id === Number.parseInt(idUsuarioGuardado, 10))
      if (usuarioDefault) {
        setUsuarioSeleccionado({
          label: `${usuarioDefault.usuario} ${usuarioDefault.nombre || ""} ${usuarioDefault.apellido || ""}`,
          ...usuarioDefault,
        })
      }
    }
  }, [usuarios])

  // Efecto para búsqueda de grupos (items con esServicio=false)
  useEffect(() => {
    const buscarGrupo = async () => {
      if (searchTextGrupo.length >= 3) {
        const resultado = await getItemTresLetras(searchTextGrupo)
        // Filtrar solo grupos (esServicio = false)
        if (resultado && resultado.items) {
          const gruposFiltrados = resultado.items.filter(item => item.esServicio === false)
          arrayItemTresLetras(gruposFiltrados)
        }
      } else {
        arrayItemTresLetras([])
      }
    }

    buscarGrupo()
  }, [searchTextGrupo])

  // Efecto para búsqueda de pasajeros
  useEffect(() => {
    if (searchTextPasajero.length >= 3) {
      getEntidadesClientesTresLetras(searchTextPasajero, 1)
    }
  }, [searchTextPasajero])

  // Efecto para habilitar/deshabilitar botón
  useEffect(() => {
    if (pasajerosList.length > 0 && selectedGrupo && selectedDestino && usuarioSeleccionado) {
      setIsButtonDisabled(false)
    } else {
      setIsButtonDisabled(true)
    }
  }, [pasajerosList, selectedGrupo, selectedDestino, usuarioSeleccionado])

  // Efecto para calcular totales
  useEffect(() => {
    const calcularTotales = () => {
      const subtotal = pasajerosList.reduce((total, pasajero) => total + pasajero.precio, 0)
      setPrecioBaseTotal(subtotal)

      // ✅ Calcular el monto total de descuentos aplicados a cada pasajero
      // Usar siempre el precio base (que nunca cambia) para calcular descuentos
      const totalDescuentos = pasajerosList.reduce((total, pasajero) => {
        const precioBase = pasajero.precio || 0 // Precio base siempre fijo
        
        // Si está liberado, el descuento es el 100% del precio base
        if (pasajero.liberado === true) {
          return total + precioBase
        }
        
        // Si tiene descuento porcentual, calcular el monto del descuento sobre el precio base
        const descuentoPorcentual = pasajero.descuento || 0
        if (descuentoPorcentual > 0) {
          const montoDescuento = precioBase * (descuentoPorcentual / 100)
          return total + montoDescuento
        }
        
        return total
      }, 0)
      
      setMontoTotalDescuentos(totalDescuentos)

      // Aplicar descuentos de transacción (valor fijo y porcentaje)
      const valorDesc = Number(Number.parseFloat(tempValorDescuento).toFixed(2)) || 0
      const porcentajeDesc = Number(Number.parseFloat(tempPorcentajeDescuento).toFixed(2)) || 0

      let totalConDescuento = subtotal

      if (valorDesc > 0) {
        totalConDescuento -= valorDesc
      }

      if (porcentajeDesc > 0) {
        const porcentajeDescuentoMonto = (totalConDescuento * porcentajeDesc) / 100
        totalConDescuento -= porcentajeDescuentoMonto
      }

      if (totalConDescuento < 0) {
        totalConDescuento = 0
      }

      setPrecioTotal(totalConDescuento)
    }

    calcularTotales()
  }, [pasajerosList, tempValorDescuento, tempPorcentajeDescuento])

  // Función para agregar pasajero (acepta pasajero como parámetro opcional)
  const handleAddPasajero = async (pasajeroToAdd = null) => {
    try {
      if (!selectedGrupo || !selectedDestino) {
        showSnackbar("Debe seleccionar un grupo y un destino primero", "warning")
        return
      }

      // Validar capacidad: no permitir agregar si se supera el total de butacas
      if (!validarCapacidadDisponible()) return

      // Usar el pasajero pasado como parámetro, o el del estado si no se proporciona
      const pasajero = pasajeroToAdd || selectedPasajero

      if (!pasajero) {
        showSnackbar("Debe seleccionar un pasajero", "warning")
        return
      }

      // Verificar si el pasajero ya está en la lista
      const yaExiste = pasajerosList.some(p => p.idEntidad === pasajero.id)
      if (yaExiste) {
        showSnackbar("Este pasajero ya está agregado al viaje", "warning")
        setSelectedPasajero(null)
        setSearchTextPasajero("")
        return
      }

 // ✅ Precio base del paquete (Paquete.monto). Si es 0, el cliente se asocia igual; el monto se puede agregar después.
      let precioPasajero = Number(precioPaqueteActual || 0)
      if (precioPaqueteLoading || precioPasajero === 0) {
        const precioActualizado = await refrescarPrecioPaquete(
          selectedGrupo?.id,
          selectedDestino?.id
        )
        precioPasajero = Number(precioActualizado || 0)
      }

      const nuevoPasajero = {
        idEntidad: pasajero.id,
        nombre: pasajero.descripcion,
        apellido: pasajero.apellido,
        dniCuitCuil: pasajero.dniCuitCuil,
        precio: precioPasajero,
        descuento: 0,
        viaja: false, // Default: false
        cuotasPagadas: 0,
        liberado: false, // Default: false según el modelo
        cuotas: null, // Default: null según el modelo
        overridePlan: false,
      }

      setPasajerosList([
        normalizarPasajero(nuevoPasajero),
        ...pasajerosList.map((p) => normalizarPasajero(p)),
      ])
      setSelectedPasajero(null)
      setSearchTextPasajero("")
      
      showSnackbar(`Pasajero "${nuevoPasajero.nombre} ${nuevoPasajero.apellido}" agregado`, "success")

      // Enfocar de nuevo el autocomplete
      if (autocompletePasajeroRef.current) {
        setTimeout(() => {
          autocompletePasajeroRef.current.focus()
        }, 100)
      }
    } catch (error) {
      console.error("Error al agregar pasajero:", error)
      showSnackbar("Error al agregar el pasajero", "error")
    }
  }

  // Función para eliminar pasajero (solo marca para eliminar, no elimina de BD hasta actualizar)
  const handleDeletePasajero = async (index) => {
    const pasajeroToRemove = pasajerosList[index]
    if (!pasajeroToRemove) return

    // Regla: no permitir eliminar si tiene cuotas pagadas activas.
    // En ese caso, debe usarse el estado "viaja" para desactivar/activar operativamente.
    if (tieneCuotasPagadas(pasajeroToRemove)) {
      showSnackbar(
        `No se puede eliminar a "${pasajeroToRemove.nombre} ${pasajeroToRemove.apellido}" porque tiene cuotas pagadas. Puede desmarcar "Viaja".`,
        "warning"
      )
      return
    }

    // Para pasajeros nuevos, simplemente eliminar de la lista
    if (!pasajeroToRemove.esExistente && !pasajeroToRemove.idTransaccion) {
      setPasajerosList(
        pasajerosList
          .filter((_, i) => i !== index)
          .map((p) => normalizarPasajero(p))
      )
      showSnackbar(`Pasajero "${pasajeroToRemove.nombre} ${pasajeroToRemove.apellido}" eliminado`, "info")
      return
    }
    
    // Para pasajeros existentes, marcarlos como "para eliminar" sin eliminar de BD aún
    const updatedList = pasajerosList.map((p, i) => {
      if (i === index) {
        return {
          ...p,
          marcadoParaEliminar: true
        }
      }
      return p
    })
    
    setPasajerosList(updatedList.map((p) => normalizarPasajero(p)))
    showSnackbar(
      `Pasajero "${pasajeroToRemove.nombre} ${pasajeroToRemove.apellido}" marcado para eliminar. Presione "Actualizar" para confirmar.`,
      "warning"
    )
  }

  // Función para eliminar todos los pasajeros
  const handleDeleteAllPasajeros = () => {
    if (pasajerosList.length === 0) {
      showSnackbar("No hay pasajeros para eliminar", "info")
      return
    }

    setPasajerosList([])
    showSnackbar("Todos los pasajeros han sido eliminados", "info")
  }

  // Función para actualizar descuento de pasajero
  const handleUpdatePasajeroDescuento = (index, newDescuento) => {
    const updatedList = [...pasajerosList]
    updatedList[index].descuento = Number(newDescuento) || 0
    setPasajerosList(updatedList.map((p) => normalizarPasajero(p)))
  }

  // Función para aplicar descuentos
  const applyDiscounts = () => {
    const valorDesc = Number(Number.parseFloat(tempValorDescuento).toFixed(2)) || 0
    const porcentajeDesc = Number(Number.parseFloat(tempPorcentajeDescuento).toFixed(2)) || 0

    // Si hay descuento en porcentaje, aplicarlo a todos los pasajeros
    if (porcentajeDesc > 0) {
      const updatedList = pasajerosList.map(pasajero => ({
        ...pasajero,
        descuento: porcentajeDesc
      }))
      setPasajerosList(updatedList.map((p) => normalizarPasajero(p)))
      showSnackbar(`Descuento del ${porcentajeDesc}% aplicado a todos los pasajeros`, "success")
    }
  }

  // Función para abrir modal de crear pasajero
  const handleAbrirModalCrearPasajero = () => {
    if (!selectedGrupo || !selectedDestino) {
      showSnackbar("Debe seleccionar un grupo y un destino primero", "warning")
      return
    }
    if (!validarCapacidadDisponible()) return
    setAbrirModalCrearPasajero(true)
    setIsEditing(false)
    setPasajeroAEditar(null)
  }

  // Función para cerrar modal de crear pasajero
  const handleCerrarModalCrearPasajero = () => {
    setAbrirModalCrearPasajero(false)
    setPasajeroAEditar(null)
  }

// Función para manejar cuando se crea un nuevo pasajero
const handlePasajeroCreado = async (pasajeroData) => {
  console.log("Pasajero creado:", pasajeroData)

  if (!validarCapacidadDisponible()) {
    return
  }
  
  // Agregar automaticamente el pasajero recien creado a la tabla
  await handleAddPasajero(pasajeroData)
  
  // handleAddPasajero ya limpia selectedPasajero y muestra mensaje de exito
}

  // Función para manejar alerta de creación/actualización de pasajero
  const handleAlerta = (exito, message = null) => {
    if (exito) {
      showSnackbar("Pasajero creado/actualizado correctamente", "success")
    } else {
      showSnackbar(message || "Error al crear/actualizar el pasajero", "error")
    }
  }

  const obtenerPrecioReferenciaDefault = () => {
    if (precioPaqueteActual > 0) return Math.round(precioPaqueteActual)
    if (pasajerosList.length > 0) {
      // Usar el precio base del pasajero (no el precio neto), que no cambia aunque esté liberado
      const suma = pasajerosList.reduce((acc, pas) => acc + (pas.precio || 0), 0)
      return Math.round(suma / pasajerosList.length)
    }
    if (selectedGrupo?.precio) return Math.round(selectedGrupo.precio)
    return 0
  }

  const normalizarDetallesParaEnvio = (detalles) =>
    detalles.map((det) => ({
      cuotaNro: det.cuotaNro,
      montoBase: det.montoBase ?? 0,
      montoDescuento: det.montoDescuento ?? 0,
      montoInteres: det.montoInteres ?? 0,
      montoTotal: det.montoTotal ?? ((det.montoBase ?? 0) + (det.montoInteres ?? 0) - (det.montoDescuento ?? 0)),
      fechaPrimerVto: det.fechaPrimerVto ? toIsoDate(det.fechaPrimerVto) : null,
      fechaSegundoVto: det.fechaSegundoVto ? toIsoDate(det.fechaSegundoVto) : null,
      descripcion: det.descripcion ?? null,
      redondeoManual: det.redondeoManual ?? null,
      // ✅ Incluir campos metadata para mantener trazabilidad
      factorEscala: det.factorEscala ?? null,
      idPlan: det.idPlan ?? planCuotasState.planId ?? null,
      idPlanDetalle: det.idPlanDetalle ?? null,
      versionPlan: det.versionPlan ?? planCuotasState.header?.version ?? null,
    }))

  const prepararHeaderPlan = (header) => ({
    idItem: header.idItem,
    idUbicacion: header.idUbicacion,
    precioBaseReferencia: Number(header.precioBaseReferencia || 0),
    cantidadCuotas: Number(header.cantidadCuotas || 0),
    porcentajeInteres: Number(header.porcentajeInteres || 0),
    porcentajeDescuento: Number(header.porcentajeDescuento || 0),
    interesPrimeraUltima: !!header.interesPrimeraUltima,
    notas: header.notas || null,
  })

  const handleCerrarPlanCuotas = async () => {
    setAbrirModalCuotasMasivo(false)
    setPlanCuotasState({
      open: false,
      loading: false,
      header: null,
      detalles: [],
      planId: null,
      mode: "global",
      targetPasajero: null,
    })
    planPrecioBaseRefInicial.current = null
    setPlanTotales({ base: 0, descuento: 0, interes: 0, total: 0 })
    setPlanError(null)
    // 🔄 Recargar pasajeros para reflejar cambios de precios sincronizados
    console.log("🔄 Recargando pasajeros tras cerrar modal de cuotas...")
    await cargarPasajerosExistentes()
  }

  const handleAbrirPlanCuotas = async (modo = "global", pasajero = null) => {
    if (modo === "global" && (!selectedGrupo || !selectedDestino)) {
      showSnackbar("Debe seleccionar un grupo y un destino antes de configurar cuotas", "warning")
      return
    }

    if (modo === "override" && !pasajero) {
      showSnackbar("No se seleccionó un pasajero válido para personalizar cuotas", "error")
      return
    }

    setPlanError(null)
    setAbrirModalCuotasMasivo(true)
    setPlanCuotasState({
      open: true,
      loading: true,
      header: null,
      detalles: [],
      planId: null,
      mode: modo,
      targetPasajero: pasajero,
    })

    try {
      const idItem = selectedGrupo?.id ?? pasajero?.idItem
      const idUbicacion = selectedDestino?.id ?? pasajero?.idUbicacion
      const planActual = idItem && idUbicacion ? await getPlanCuotas(idItem, idUbicacion) : null

      let header = null
      let detalles = []
      let planId = planActual?.id ?? null

      if (modo === "global") {
        if (planActual) {
          header = {
            idItem: planActual.idItem,
            idUbicacion: planActual.idUbicacion,
            precioBaseReferencia: planActual.precioBaseReferencia,
            cantidadCuotas: planActual.cantidadCuotas,
            porcentajeInteres: planActual.porcentajeInteres,
            porcentajeDescuento: planActual.porcentajeDescuento ?? 0,
            interesPrimeraUltima: planActual.interesPrimeraUltima,
            notas: planActual.notas ?? "",
            version: planActual.version,
          }
          
          // ✅ CARGAR fechas TAL CUAL están en la BD (respetando cambios manuales previos)
          // Las fechas solo se regeneran cuando el usuario hace click en "Recalcular plan"
          detalles = planActual.detalles.map((det) => ({
            cuotaNro: det.cuotaNro,
            montoBase: det.montoBase ?? 0,
            montoDescuento: det.montoDescuento ?? 0,
            montoInteres: det.montoInteres ?? 0,
            montoTotal: det.montoTotal ?? ((det.montoBase ?? 0) + (det.montoInteres ?? 0) - (det.montoDescuento ?? 0)),
            fechaPrimerVto: toDateOnly(det.fechaPrimerVto),
            fechaSegundoVto: toDateOnly(det.fechaSegundoVto),
            descripcion: det.descripcion ?? null,
            redondeoManual: det.redondeoManual ?? null,
          }))
        } else {
          const precioReferencia = obtenerPrecioReferenciaDefault()
          const cantidadCuotasDefault = pasajerosList.length > 0
            ? (pasajerosList[0].cuotas || 6)
            : 6
          header = {
            idItem,
            idUbicacion,
            precioBaseReferencia: precioReferencia,
            cantidadCuotas: cantidadCuotasDefault,
            porcentajeInteres: 0.1,
            porcentajeDescuento: 0,
            interesPrimeraUltima: false,
            notas: "",
          }
          // En plan global nuevo, respetar corte del día actual:
          // < 20 arranca mes actual, >= 20 arranca mes siguiente
          const offsetMesInicialGlobal = obtenerOffsetMesInicioFechasFijasGlobal()
          detalles = generarPlanDetalles(
            header,
            [],
            true,
            { offsetMesInicial: offsetMesInicialGlobal }
          )
        }
      } else if (modo === "override" && pasajero) {
        // Usar el precio base del pasajero (no el precio neto), que no cambia aunque esté liberado
        const precioBasePasajero = pasajero.precio || 0
        
        // ⭐ Inicializar configuración de override con valores del plan global (si existe)
        if (planActual) {
          setOverrideConfig({
            cantidadCuotas: planActual.cantidadCuotas || 7,
            porcentajeInteres: planActual.porcentajeInteres || 10,
            porcentajeDescuento: planActual.porcentajeDescuento || 0,
            interesPrimeraUltima: planActual.interesPrimeraUltima || false,
          })
        } else {
          setOverrideConfig({
            cantidadCuotas: 7,
            porcentajeInteres: 10,
            porcentajeDescuento: 0,
            interesPrimeraUltima: false,
          })
        }
        
        // 🔧 FIX: Primero intentar cargar las cuotas personalizadas existentes del pasajero
        console.log(`🔍 Cargando cuotas existentes para pasajero ${pasajero.idEntidad}...`)
        const cuotasPasajero = await getCuentaCorrienteCuotas(
          pasajero.idEntidad,
          idItem,
          idUbicacion
        )
        console.log(`📊 Cuotas obtenidas de BD:`, cuotasPasajero)
        
        // ✅ Verificar si alguna cuota tiene override: true (fuente de verdad)
        const tieneOverride = cuotasPasajero?.cuotas?.some(c => c.override === true)
        console.log(`🔍 ¿Tiene override en BD? ${tieneOverride}`)
        
        // Si el pasajero tiene cuotas personalizadas (override), cargarlas
        if (cuotasPasajero?.cuotas && cuotasPasajero.cuotas.length > 0 && tieneOverride) {
          console.log(`✅ Cargando ${cuotasPasajero.cuotas.length} cuotas personalizadas del pasajero`)
          
          // ⭐ NUEVO: Usar la configuración guardada en la BD (si existe)
          // Si no existe, hacer fallback al cálculo antiguo para compatibilidad
          let configFromDB = cuotasPasajero.overrideConfig;
          let porcentajeInteresAUsar; // Variable para usar en header
          let porcentajeDescuentoAUsar;
          
          if (
            configFromDB &&
            configFromDB.cantidadCuotas &&
            configFromDB.porcentajeInteres !== null
          ) {
            console.log(`✅ Usando configuración override de la BD:`, configFromDB)
            porcentajeInteresAUsar = configFromDB.porcentajeInteres;
            porcentajeDescuentoAUsar = Number(configFromDB.porcentajeDescuento || 0);
            setOverrideConfig({
              cantidadCuotas: configFromDB.cantidadCuotas,
              porcentajeInteres: configFromDB.porcentajeInteres,
              porcentajeDescuento: porcentajeDescuentoAUsar,
              interesPrimeraUltima: configFromDB.interesPrimeraUltima ?? false,
            })
          } else {
            // ⚠️ FALLBACK: Calcular el % de interés (para cuotas creadas antes de esta migración)
            console.warn(`⚠️ No hay configuración override en BD, calculando desde cuotas...`)
            const totalBase = cuotasPasajero.cuotas.reduce((sum, c) => sum + (c.montoBase ?? 0), 0)
            const totalDescuento = cuotasPasajero.cuotas.reduce((sum, c) => sum + (c.montoDescuento ?? 0), 0)
            const totalBaseNeta = Math.max(0, totalBase - totalDescuento)
            const totalInteres = cuotasPasajero.cuotas.reduce((sum, c) => sum + (c.montoInteres ?? 0), 0)
            // Interés siempre se interpreta sobre base neta (base - descuento)
            porcentajeInteresAUsar = totalBaseNeta > 0 ? (totalInteres / totalBaseNeta) * 100 : 0
            // Descuento se interpreta sobre base bruta
            porcentajeDescuentoAUsar = totalBase > 0 ? (totalDescuento / totalBase) * 100 : 0
            
            setOverrideConfig({
              cantidadCuotas: cuotasPasajero.cuotas.length,
              porcentajeInteres: porcentajeInteresAUsar,
              porcentajeDescuento: porcentajeDescuentoAUsar,
              interesPrimeraUltima: planActual?.interesPrimeraUltima ?? false,
            })
            console.log(`🔧 overrideConfig calculado (fallback):`, {
              cantidadCuotas: cuotasPasajero.cuotas.length,
              porcentajeInteres: porcentajeInteresAUsar,
              porcentajeDescuento: porcentajeDescuentoAUsar,
            })
          }
          
          // Cargar las cuotas personalizadas existentes
          header = {
            idItem,
            idUbicacion,
            precioBaseReferencia: precioBasePasajero,
            cantidadCuotas: cuotasPasajero.cuotas.length,
            porcentajeInteres: porcentajeInteresAUsar,
            porcentajeDescuento: porcentajeDescuentoAUsar,
            interesPrimeraUltima: planActual?.interesPrimeraUltima ?? false,
            notas: planActual?.notas ?? "",
            version: planActual?.version ?? 1,
          }
          detalles = cuotasPasajero.cuotas.map((cuota) => ({
            cuotaNro: cuota.cuotaNro,
            montoBase: cuota.montoBase ?? 0,
            montoDescuento: cuota.montoDescuento ?? 0,
            montoInteres: cuota.montoInteres ?? 0,
            montoTotal: (cuota.montoBase ?? 0) + (cuota.montoInteres ?? 0) - (cuota.montoDescuento ?? 0),
            fechaPrimerVto: toDateOnly(cuota.fechaPrimerVto),
            fechaSegundoVto: toDateOnly(cuota.fechaSegundoVto),
            descripcion: cuota.descripcion ?? null,
            redondeoManual: cuota.redondeoManual ?? null,
            // ✅ Incluir campos metadata desde la BD
            factorEscala: cuota.factorEscala ?? null,
            idPlan: cuota.idPlan ?? null,
            idPlanDetalle: cuota.idPlanDetalle ?? null,
            versionPlan: cuota.versionPlan ?? null,
            // ✅ Incluir idTransaccion para detectar cuotas pagadas
            idTransaccion: cuota.idTransaccion ?? null,
          }))
          console.log(`📝 Detalles cargados:`, detalles)
        } else if (planActual) {
          // Si no tiene cuotas personalizadas, escalar el plan general
          header = {
            idItem: planActual.idItem,
            idUbicacion: planActual.idUbicacion,
            precioBaseReferencia: planActual.precioBaseReferencia,
            cantidadCuotas: planActual.cantidadCuotas,
            porcentajeInteres: planActual.porcentajeInteres,
            porcentajeDescuento: planActual.porcentajeDescuento ?? 0,
            interesPrimeraUltima: planActual.interesPrimeraUltima,
            notas: planActual.notas ?? "",
            version: planActual.version,
          }
          const totalesPlan = calcularTotalesPlanLocal(planActual.detalles)
          const factor = planActual.precioBaseReferencia
            ? precioBasePasajero / planActual.precioBaseReferencia
            : 1
          detalles = escalarPlanParaPasajero(
            planActual.detalles,
            factor,
            totalesPlan
          )
        } else {
          // Si no hay plan general, crear uno nuevo
          header = {
            idItem: selectedGrupo?.id,
            idUbicacion: selectedDestino?.id,
            precioBaseReferencia: precioBasePasajero,
            cantidadCuotas: pasajero.cuotas || 1,
            porcentajeInteres: 0,
            porcentajeDescuento: 0,
            interesPrimeraUltima: false,
            notas: "",
          }
          detalles = generarPlanDetalles(header)
        }
      }

      if (modo === "global") {
        planPrecioBaseRefInicial.current = header?.precioBaseReferencia ?? null
      }
      actualizarPlanDetalles(detalles)
      setPlanCuotasState((prev) => ({
        ...prev,
        loading: false,
        header,
        detalles,
        planId,
        mode: modo,
        targetPasajero: pasajero,
      }))
    } catch (error) {
      console.error("Error al preparar el plan de cuotas:", error)
      setPlanError("No se pudo cargar el plan de cuotas")
      showSnackbar("No se pudo cargar el plan de cuotas", "error")
      setPlanCuotasState((prev) => ({
        ...prev,
        loading: false,
      }))
    }
  }

  const handlePlanHeaderChange = (campo, valor) => {
    setPlanCuotasState((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        [campo]: valor,
      },
    }))
  }

  const handleRecalcularPlan = () => {
    if (!planCuotasState.header) return
    const offsetMesInicial = obtenerOffsetMesInicioFechasFijasGlobal()
    const detalles = generarPlanDetalles(
      planCuotasState.header,
      planCuotasState.detalles,
      true,
      { offsetMesInicial }
    ) // true = usar fechas fijas con regla de corte del día 20 (modo global)
    actualizarPlanDetalles(detalles)
  }

  // ⭐ NUEVA FUNCIÓN: Recalcular plan para un pasajero individual
  const handleRecalcularPlanPasajero = () => {
    const pasajero = planCuotasState.targetPasajero
    if (!pasajero) {
      showSnackbar("No se encontró el pasajero para recalcular", "error")
      return
    }

    // Obtener el monto neto del pasajero (con descuento aplicado)
    const montoNeto = calcularPrecioNetoPasajero(pasajero)
    
    if (montoNeto <= 0) {
      showSnackbar("El pasajero tiene monto $0 (liberado). No se generan cuotas.", "warning")
      return
    }

    // ⚠️ VALIDACIÓN: Verificar si hay cuotas pagadas
    const cuotasActuales = planCuotasState.detalles || []
    const cuotasPagadas = cuotasActuales.filter(c => c.idTransaccion !== null && c.idTransaccion !== undefined)
    
    if (cuotasPagadas.length > 0) {
      const ultimaCuotaPagada = Math.max(...cuotasPagadas.map(c => c.cuotaNro))
      const cantidadNueva = overrideConfig.cantidadCuotas
      
      // Si intenta crear menos cuotas que las que ya están pagadas
      if (cantidadNueva <= ultimaCuotaPagada) {
        alert(
          `⚠️ NO SE PUEDE RECALCULAR\n\n` +
          `Este pasajero tiene ${cuotasPagadas.length} cuota(s) pagada(s) hasta la cuota #${ultimaCuotaPagada}.\n\n` +
          `No puedes configurar ${cantidadNueva} cuota(s) porque ya hay cuotas pagadas.\n\n` +
          `Debes configurar al menos ${ultimaCuotaPagada + 1} cuota(s) o más.`
        )
        return
      }
      
      // Mostrar advertencia pero permitir continuar
      const confirmar = window.confirm(
        `⚠️ ADVERTENCIA: Cuotas Pagadas\n\n` +
        `Este pasajero tiene ${cuotasPagadas.length} cuota(s) pagada(s):\n` +
        `- Última cuota pagada: #${ultimaCuotaPagada}\n\n` +
        `Al recalcular:\n` +
        `✅ Las cuotas pagadas (#1 a #${ultimaCuotaPagada}) NO se modificarán\n` +
        `🔄 Solo se recalcularán las cuotas pendientes (desde #${ultimaCuotaPagada + 1})\n\n` +
        `¿Deseas continuar?`
      )
      
      if (!confirmar) {
        return
      }
    }

    // Crear un header temporal con los valores de override
    const headerTemporal = {
      precioBaseReferencia: montoNeto,
      cantidadCuotas: overrideConfig.cantidadCuotas,
      porcentajeInteres: overrideConfig.porcentajeInteres,
      porcentajeDescuento: overrideConfig.porcentajeDescuento,
      interesPrimeraUltima: overrideConfig.interesPrimeraUltima,
    }

    // Generar el plan con fechas fijas (día 20 y 30/28)
    // ⭐ Pasar detalles actuales para preservar cuotas pagadas (generarPlanDetalles las mantiene)
    const offsetMesInicial = obtenerOffsetMesInicioFechasFijasGlobal()
    const detallesGenerados = generarPlanDetalles(
      headerTemporal,
      cuotasActuales,
      true,
      { offsetMesInicial }
    )

    // Actualizar los detalles en el estado
    actualizarPlanDetalles(detallesGenerados)
    
    showSnackbar(
      `Plan recalculado: ${overrideConfig.cantidadCuotas} cuota(s) por $${formatNumberArgentina(montoNeto)}`,
      "success"
    )
  }

  const handlePlanDetalleChange = (index, campo, valor) => {
    const detalles = [...planCuotasState.detalles]
    const detalleActual = { ...detalles[index] }

    if (["montoBase", "montoDescuento", "montoInteres", "montoTotal"].includes(campo)) {
      const numero = Math.round(Number(valor) || 0)
      detalleActual[campo] = numero
      const base = Math.max(0, Math.round(Number(detalleActual.montoBase || 0)))
      const descuentoNormalizado = Math.max(0, Math.round(Number(detalleActual.montoDescuento || 0)))
      const descuento = Math.min(descuentoNormalizado, base)
      const baseNeta = Math.max(0, base - descuento)

      detalleActual.montoBase = base
      detalleActual.montoDescuento = descuento

      if (campo === "montoTotal") {
        const totalInput = Math.max(0, Math.round(Number(detalleActual.montoTotal || 0)))
        const interesCalculado = Math.max(0, totalInput - baseNeta)
        detalleActual.montoInteres = interesCalculado
        detalleActual.montoTotal = baseNeta + interesCalculado
      } else {
        const interes = Math.max(0, Math.round(Number(detalleActual.montoInteres || 0)))
        detalleActual.montoInteres = interes
        detalleActual.montoTotal = baseNeta + interes
      }
    } else if (campo === "fechaPrimerVto" || campo === "fechaSegundoVto") {
      detalleActual[campo] = valor ? toIsoDate(valor) : null
    } else {
      detalleActual[campo] = valor
    }

    detalles[index] = detalleActual
    actualizarPlanDetalles(detalles)
  }

  const persistirPlanCuotas = async () => {
    if (!planCuotasState.header) return null
    setPlanGuardando(true)
    try {
      const payload = {
        header: prepararHeaderPlan(planCuotasState.header),
        detalles: normalizarDetallesParaEnvio(planCuotasState.detalles),
      }
      await postPlanCuotas(payload)
      const refrescado = await getPlanCuotas(
        planCuotasState.header.idItem,
        planCuotasState.header.idUbicacion
      )
      if (refrescado) {
          const detallesRefrescados = refrescado.detalles.map((det) => ({
            cuotaNro: det.cuotaNro,
            montoBase: det.montoBase ?? 0,
            montoDescuento: det.montoDescuento ?? 0,
            montoInteres: det.montoInteres ?? 0,
            montoTotal: det.montoTotal ?? ((det.montoBase ?? 0) + (det.montoInteres ?? 0) - (det.montoDescuento ?? 0)),
            fechaPrimerVto: toDateOnly(det.fechaPrimerVto),
            fechaSegundoVto: toDateOnly(det.fechaSegundoVto),
            descripcion: det.descripcion ?? null,
          redondeoManual: det.redondeoManual ?? null,
        }))
        actualizarPlanDetalles(detallesRefrescados)
        setPlanCuotasState((prev) => ({
          ...prev,
          planId: refrescado.id,
          header: {
            idItem: refrescado.idItem,
            idUbicacion: refrescado.idUbicacion,
            precioBaseReferencia: refrescado.precioBaseReferencia,
            cantidadCuotas: refrescado.cantidadCuotas,
            porcentajeInteres: refrescado.porcentajeInteres,
            porcentajeDescuento: refrescado.porcentajeDescuento ?? 0,
            interesPrimeraUltima: refrescado.interesPrimeraUltima,
            notas: refrescado.notas ?? "",
            version: refrescado.version,
          },
        }))
        await refrescarPrecioPaquete(refrescado.idItem, refrescado.idUbicacion)
        // 🔄 SINCRONIZACIÓN: Informar al usuario que el precio del paquete también fue actualizado
        showSnackbar(
          "Plan de cuotas guardado. El precio del paquete fue sincronizado automáticamente.",
          "success"
        )
        console.log(`✅ Plan de cuotas guardado y sincronizado con precio del paquete: $${refrescado.precioBaseReferencia}`)
        return refrescado
      }
      return null
    } catch (error) {
      console.error("Error al guardar el plan de cuotas:", error)
      setPlanError("No se pudo guardar el plan de cuotas")
      showSnackbar("Error al guardar el plan de cuotas", "error")
      return null
    } finally {
      setPlanGuardando(false)
    }
  }

  const handleGuardarPlan = async () => {
    if (esSoloLectura) {
      showSnackbar("Modo solo lectura: el paquete está cerrado.", "warning")
      return
    }
    await persistirPlanCuotas()
    // 🔄 Recargar pasajeros para reflejar los precios actualizados
    console.log("🔄 Recargando pasajeros tras guardar plan de cuotas...")
    await cargarPasajerosExistentes()
    await refrescarPrecioPaquete()
  }

  const handleAplicarPlanGlobal = async () => {
    let plan = planCuotasState.planId
    const precioActual = Number(planCuotasState.header?.precioBaseReferencia || 0)
    const precioInicial = planPrecioBaseRefInicial.current
    const precioCambio = precioInicial !== null && precioInicial !== precioActual

    if (!plan || precioCambio) {
      const resultado = await persistirPlanCuotas()
      plan = resultado?.id
      if (!plan) return
      planPrecioBaseRefInicial.current = precioActual
    }

    try {
      setPlanGuardando(true)
      const pasajerosPayload = pasajerosList.map((pasajero) => ({
        idEntidad: pasajero.idEntidad,
        precio: calcularPrecioNetoPasajero(pasajero),
        override: pasajero.overridePlan,
      }))

      await aplicarPlanCuotas(plan, pasajerosPayload)

      const updated = pasajerosList.map((pasajero) =>
        pasajero.overridePlan
          ? pasajero
          : normalizarPasajero({
              ...pasajero,
              cuotas: planCuotasState.detalles.length,
              overridePlan: false,
              planAplicado: true,
            })
      )
      setPasajerosList(updated)

      showSnackbar("Plan de cuotas aplicado a los pasajeros", "success")
      await refrescarPrecioPaquete()
      handleCerrarPlanCuotas()
    } catch (error) {
      console.error("Error al aplicar plan de cuotas:", error)
      showSnackbar("Error al aplicar el plan de cuotas", "error")
    } finally {
      setPlanGuardando(false)
    }
  }

  const handleGuardarOverridePasajero = async () => {
    if (esSoloLectura) {
      showSnackbar("Modo solo lectura: el paquete está cerrado.", "warning")
      return
    }
    const pasajero = planCuotasState.targetPasajero
    if (!pasajero) return
    if (!selectedGrupo || !selectedDestino) {
      showSnackbar("Seleccione un grupo y destino antes de guardar override", "warning")
      return
    }

    try {
      setPlanGuardando(true)
      const montoTotal = calcularPrecioNetoPasajero(pasajero)
      // ⭐ Filtrar: enviar solo cuotas impagas (el backend rechaza si incluimos las pagadas)
      const detallesCompletos = planCuotasState.detalles || []
      const cuotasPagadas = detallesCompletos.filter(c => c.idTransaccion != null && c.idTransaccion !== undefined)
      const ultimaCuotaPagada = cuotasPagadas.length > 0 ? Math.max(...cuotasPagadas.map(c => c.cuotaNro)) : 0
      const detallesAEnviar = ultimaCuotaPagada > 0
        ? detallesCompletos.filter(d => d.cuotaNro > ultimaCuotaPagada)
        : detallesCompletos
      console.log("📤 Guardar override:", {
        ultimaCuotaPagada,
        totalDetalles: detallesCompletos.length,
        enviando: detallesAEnviar.length,
        cuotasNro: detallesAEnviar.map(d => d.cuotaNro),
      })
      await postCuentaCorrienteCuotasDetalle({
        idEntidad: pasajero.idEntidad,
        idItem: selectedGrupo.id,
        idUbicacion: selectedDestino.id,
        montoTotal,
        override: true,
        detalles: normalizarDetallesParaEnvio(detallesAEnviar),
        // ⭐ NUEVOS: Enviar parámetros de configuración override al backend
        cantidadCuotasOverride: overrideConfig.cantidadCuotas,
        porcentajeInteresOverride: overrideConfig.porcentajeInteres,
        porcentajeDescuentoOverride: overrideConfig.porcentajeDescuento,
        interesPrimeraUltimaOverride: overrideConfig.interesPrimeraUltima,
      })

      const actualizados = pasajerosList.map((p) =>
        p.idEntidad === pasajero.idEntidad
          ? normalizarPasajero({
              ...p,
              overridePlan: true,
              cuotas: planCuotasState.detalles.length,
              planAplicado: true,
            })
          : p
      )
      setPasajerosList(actualizados)
      
      // ✅ IMPORTANTE: Recargar las cuotas desde el backend para verificar que se guardaron
      console.log("🔄 Recargando cuotas desde el backend para verificar guardado...")
      const cuotasVerificacion = await getCuentaCorrienteCuotas(
        pasajero.idEntidad,
        selectedGrupo.id,
        selectedDestino.id
      )
      console.log("✅ Cuotas recargadas desde BD:", cuotasVerificacion)
      
      showSnackbar("Cuotas personalizadas guardadas para el pasajero", "success")
      handleCerrarPlanCuotas()
    } catch (error) {
      console.error("Error al guardar cuotas personalizadas:", error)
      console.error("Respuesta del servidor:", error.response?.data)
      
      // ⚠️ Mostrar mensaje específico si hay conflicto con cuotas pagadas
      const msg = error.response?.data?.message
      if (error.response?.data?.conflicto) {
        alert(
          `⚠️ ERROR: ${msg || "Conflicto con cuotas pagadas"}\n\n` +
          `Cuotas pagadas: ${error.response.data.cuotasPagadas}\n` +
          `Última cuota pagada: #${error.response.data.ultimaCuotaPagada}`
        )
      } else if (msg) {
        showSnackbar(msg, "error")
      } else {
        showSnackbar("Error al guardar cuotas personalizadas", "error")
      }
    } finally {
      setPlanGuardando(false)
    }
  }

  // Función para generar transacciones
  const handleGenerarTransacciones = async (e) => {
    e.preventDefault()

    // 🔒 Solo lectura: permitir ver pero no guardar cambios
    if (esSoloLectura) {
      showSnackbar("Modo solo lectura: el paquete está cerrado.", "warning")
      return
    }

    setLoading(true)

    try {
      if (!selectedGrupo || !selectedDestino) {
        showSnackbar("Debe seleccionar un grupo y un destino", "error")
        setLoading(false)
        return
      }

      if (pasajerosList.length === 0) {
        showSnackbar("Debe agregar al menos un pasajero", "error")
        setLoading(false)
        return
      }

      if (!usuarioSeleccionado) {
        showSnackbar("Error: No se encontró usuario logueado", "error")
        setLoading(false)
        return
      }

      // Primero, eliminar las transacciones marcadas para eliminar
      const pasajerosMarcadosParaEliminar = pasajerosList.filter(p => p.marcadoParaEliminar && p.idTransaccion)
      let transaccionesEliminadas = 0
      
      for (const pasajero of pasajerosMarcadosParaEliminar) {
        try {
          await updateDeleteTransaccion(pasajero.idTransaccion)
          console.log(`✅ Transacción ${pasajero.idTransaccion} marcada como eliminada`)
          transaccionesEliminadas++
          
          // Eliminar de valores originales
          const nuevosValoresOriginales = { ...valoresOriginalesPasajeros }
          delete nuevosValoresOriginales[pasajero.idTransaccion]
          setValoresOriginalesPasajeros(nuevosValoresOriginales)
        } catch (error) {
          console.error(`Error al eliminar transacción ${pasajero.idTransaccion}:`, error)
          showSnackbar(`Error al eliminar pasajero ${pasajero.nombre} ${pasajero.apellido}`, "error")
        }
      }

      // Filtrar la lista para no procesar los marcados para eliminar
      const pasajerosAProcesar = pasajerosList.filter(p => !p.marcadoParaEliminar)

      // Calcular descuentos de transacción
      const valorDescTransaccion = Number.parseFloat(tempValorDescuento) || 0
      const porcentajeDescTransaccion = Number.parseFloat(tempPorcentajeDescuento) || 0

      let transaccionesCreadas = 0
      let transaccionesActualizadas = 0

      // Generar/actualizar una transacción por cada pasajero (excepto los marcados para eliminar)
      for (const pasajero of pasajerosAProcesar) {
        try {
          // Si el pasajero ya tiene idTransaccion, es existente - actualizar si hay cambios
          if (pasajero.esExistente || pasajero.idTransaccion || pasajero.id) {
            const idTransaccion = pasajero.idTransaccion ?? pasajero.id
            const valoresOriginales = valoresOriginalesPasajeros[idTransaccion]
            
            // ✅ Calcular precio final: usar precio base siempre, aplicar descuentos al total
            const precioBase = Number(precioPaqueteActual || 0) // Precio base siempre = Paquete.monto
            
            // Calcular precio con descuento individual (si está liberado, total = 0)
            let precioConDescuentoIndividual = precioBase
            if (pasajero.liberado === true) {
              precioConDescuentoIndividual = 0
            } else {
              const descuentoPorcentual = pasajero.descuento || 0
              precioConDescuentoIndividual = precioBase * (1 - descuentoPorcentual / 100)
            }
            
            // Calcular total base para distribución proporcional de descuentos de transacción
            const totalBaseTransaccion = pasajerosList.reduce((total, p) => {
              const precioBaseP = Number(precioPaqueteActual || 0)
              let precioConDescP = precioBaseP
              if (p.liberado === true) {
                precioConDescP = 0
              } else {
                precioConDescP = precioBaseP * (1 - (p.descuento || 0) / 100)
              }
              return total + precioConDescP
            }, 0)
            
            const proporcionPasajero = totalBaseTransaccion > 0 ? precioConDescuentoIndividual / totalBaseTransaccion : 0
            const descuentoFijoPasajero = valorDescTransaccion * proporcionPasajero
            const descuentoPorcentualPasajero = precioConDescuentoIndividual * (porcentajeDescTransaccion / 100)
            let precioFinalPasajero = precioConDescuentoIndividual - descuentoFijoPasajero - descuentoPorcentualPasajero
            if (precioFinalPasajero < 0) precioFinalPasajero = 0
            
            const valoresActuales = {
              viaja: getViajaEfectivo(pasajero),
              liberado: pasajero.liberado === true,
              cuotas: pasajero.cuotas || null,
              descuento: pasajero.descuento || 0,
              precio: precioFinalPasajero,
            }
            
            // Verificar si hay cambios (incluyendo descuento y precio)
            const hayCambios = !valoresOriginales || 
              valoresOriginales.viaja !== valoresActuales.viaja ||
              valoresOriginales.liberado !== valoresActuales.liberado ||
              valoresOriginales.cuotas !== valoresActuales.cuotas ||
              valoresOriginales.descuento !== valoresActuales.descuento ||
              Math.abs((valoresOriginales.precio || 0) - valoresActuales.precio) > 0.01 // Tolerancia para comparación de decimales
            
            if (hayCambios) {
              console.log(`🔄 Actualizando transacción ${idTransaccion} para ${pasajero.nombre} ${pasajero.apellido}`)
              console.log(`   Cambios detectados:`, {
                viaja: valoresOriginales?.viaja !== valoresActuales.viaja,
                liberado: valoresOriginales?.liberado !== valoresActuales.liberado,
                cuotas: valoresOriginales?.cuotas !== valoresActuales.cuotas,
                descuento: valoresOriginales?.descuento !== valoresActuales.descuento,
                precio: Math.abs((valoresOriginales?.precio || 0) - valoresActuales.precio) > 0.01,
              })
              
              const descripcionTransaccion = `Viaje: ${selectedGrupo.descripcion} - Destino: ${selectedDestino.label} - Pasajero: ${pasajero.nombre} ${pasajero.apellido}`
              
              // Actualizar transacción (incluyendo montoTotal)
              await putTransaccion(
                idTransaccion,
                precioFinalPasajero,
                7, // Tipo transacción: paquete (mantener tipo 7, no convertir a venta)
                pasajero.idEntidad,
                null, // idCategorizacion
                descripcionTransaccion,
                usuarioSeleccionado.id,
                selectedDestino.id,
                1, // Negocio por defecto
                0, // montoDescuento
                0, // porcentajeDescuento
                valoresActuales.viaja, // viaja
                valoresActuales.liberado, // liberado
                valoresActuales.cuotas, // cuotas
              )
              
              // Siempre actualizar TransaccionItem si hay cambios en descuento o precio
              // ✅ Guardar precio base en precio, y precio final en montoTotal
              await updateTransaccionItem(
                idTransaccion,
                selectedGrupo.id,
                {
                  cantidad: 1, // Siempre 1 pasajero
                  precio: precioBase, // Precio base siempre fijo
                  porcentajeDescuento: pasajero.liberado === true ? 100 : (pasajero.descuento || 0),
                  montoTotal: precioFinalPasajero, // El precio final con todos los descuentos aplicados
                }
              )
              
              transaccionesActualizadas++
              console.log(`✅ Transacción ${idTransaccion} actualizada`)
            } else {
              console.log(`ℹ️ Pasajero ${pasajero.nombre} ${pasajero.apellido} sin cambios`)
            }
            continue
          }

          // Solo crear transacciones para pasajeros NUEVOS
          // ✅ Calcular precio final: usar precio base siempre, aplicar descuentos al total
          const precioBase = Number(precioPaqueteActual || 0) // Precio base siempre = Paquete.monto
          
          // Calcular precio con descuento individual (si está liberado, total = 0)
          let precioConDescuentoIndividual = precioBase
          if (pasajero.liberado === true) {
            precioConDescuentoIndividual = 0
          } else {
            const descuentoPorcentual = pasajero.descuento || 0
            precioConDescuentoIndividual = precioBase * (1 - descuentoPorcentual / 100)
          }

          // Calcular total base para distribución proporcional de descuentos de transacción
          const totalBaseTransaccion = pasajerosList.reduce((total, p) => {
            const precioBaseP = Number(precioPaqueteActual || 0)
            let precioConDescP = precioBaseP
            if (p.liberado === true) {
              precioConDescP = 0
            } else {
              precioConDescP = precioBaseP * (1 - (p.descuento || 0) / 100)
            }
            return total + precioConDescP
          }, 0)

          const proporcionPasajero = totalBaseTransaccion > 0 ? precioConDescuentoIndividual / totalBaseTransaccion : 0
          
          const descuentoFijoPasajero = valorDescTransaccion * proporcionPasajero
          const descuentoPorcentualPasajero = precioConDescuentoIndividual * (porcentajeDescTransaccion / 100)

          let precioFinalPasajero = precioConDescuentoIndividual - descuentoFijoPasajero - descuentoPorcentualPasajero
          
          if (precioFinalPasajero < 0) {
            precioFinalPasajero = 0
          }

          // Crear transacción
          const descripcionTransaccion = `Viaje: ${selectedGrupo.descripcion} - Destino: ${selectedDestino.label} - Pasajero: ${pasajero.nombre} ${pasajero.apellido}`

          const transaccionResponse = await postTransaccion(
            precioFinalPasajero,
            7, // Tipo transacción: paquete
            pasajero.idEntidad,
            null,
            descripcionTransaccion,
            usuarioSeleccionado.id,
            selectedDestino.id,
            1, // Negocio por defecto
            0, // Monto descuento (ya aplicado en el precio)
            0, // Porcentaje descuento (ya aplicado en el precio)
            null, // transaccionAsociada
            null, // fecha
            false, // afectaCuentaCorriente
            null, // clientOS
            getViajaEfectivo(pasajero), // viaja
            pasajero.liberado === true, // liberado (default: false si no está definido)
            pasajero.cuotas || null, // cuotas
          )

          const idTransaccion = transaccionResponse.data.id

          // Crear TransaccionItem
          // ✅ Pasar precio base y porcentaje de descuento correcto
          // El backend calculará montoTotal = precioBase * cantidad * (1 - descuento/100)
          await postTransaccionItem(
            idTransaccion,
            selectedGrupo.id,
            1, // Cantidad: 1 pasajero
            precioBase, // Precio base siempre fijo
            pasajero.liberado === true ? 100 : (pasajero.descuento || 0) // 100% si está liberado
          )

          transaccionesCreadas++
        } catch (error) {
          console.error(`Error al crear transacción para ${pasajero.nombre}:`, error)
          showSnackbar(`Error al crear transacción para ${pasajero.nombre} ${pasajero.apellido}`, "error")
        }
      }

      // 🔧 NOTA: Ya no filtramos aquí porque recargaremos todos los pasajeros desde la BD
      // Esto asegura que los pasajeros nuevos aparezcan con su idTransaccion correcto

      const pasajerosExistentes = pasajerosList.filter(p => p.esExistente || p.idTransaccion).length
      const pasajerosNuevos = transaccionesCreadas
      
      // Actualizar valores originales después de guardar
      if (transaccionesActualizadas > 0 || transaccionesCreadas > 0) {
        const nuevosValoresOriginales = { ...valoresOriginalesPasajeros }
        pasajerosList.forEach(p => {
          if (p.idTransaccion) {
            // ✅ Guardar precio base como precio original (siempre fijo)
            // El precio base nunca cambia, los descuentos se aplican al calcular el total
            nuevosValoresOriginales[p.idTransaccion] = {
              viaja: getViajaEfectivo(p),
              liberado: p.liberado === true,
              cuotas: p.cuotas || null,
              descuento: p.descuento || 0,
              precio: Number(precioPaqueteActual || 0), // Precio base siempre = Paquete.monto
            }
          }
        })
        setValoresOriginalesPasajeros(nuevosValoresOriginales)
      }
      
      // Remover pasajeros marcados para eliminar de la lista local
      if (transaccionesEliminadas > 0) {
        setPasajerosList(pasajerosAProcesar.map((p) => normalizarPasajero(p)))
      }
      
      // Mensajes de éxito
      const mensajes = []
      if (transaccionesCreadas > 0) mensajes.push(`${transaccionesCreadas} creada(s)`)
      if (transaccionesActualizadas > 0) mensajes.push(`${transaccionesActualizadas} actualizada(s)`)
      if (transaccionesEliminadas > 0) mensajes.push(`${transaccionesEliminadas} eliminada(s)`)
      
      if (mensajes.length > 0) {
        showSnackbar(`✅ Transacciones: ${mensajes.join(', ')}.`, "success")
      } else {
        showSnackbar("No hay cambios para guardar", "info")
      }
      
      // 🔧 RECARGAR PASAJEROS DESPUÉS DE ACTUALIZAR/CREAR/ELIMINAR
      if (transaccionesCreadas > 0 || transaccionesActualizadas > 0 || transaccionesEliminadas > 0) {
        console.log("🔄 Recargando pasajeros después de actualizar/crear/eliminar transacciones")
        await cargarPasajerosExistentes()
      }
      
    } catch (error) {
      console.error("Error al generar las transacciones:", error)
      showSnackbar("Error al generar las transacciones", "error")
    } finally {
      setLoading(false)
    }
  }

  return (
      <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: modoModal ? "auto" : "100%",
        backgroundColor: "#f5f5f5",
        position: "relative",
      }}
    >
      {esSoloLectura && (
        <Alert severity="warning" sx={{ borderRadius: 2, mb: 1 }}>
          Paquete <strong>cerrado</strong>: modo <strong>solo lectura</strong>. Podés revisar pasajeros, pero no modificar ni guardar cambios.
        </Alert>
      )}
      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          p: modoModal ? 0 : 2,
          overflow: "auto",
        }}
      >
        <form onSubmit={handleGenerarTransacciones}>
          <Grid container spacing={1.5}>
            {/* Left Column - Grupo, Destino y Pasajeros */}
            <Grid item xs={12} md={9}>
              {/* Card para seleccionar Grupo y Destino - Oculto cuando se está editando */}
              {!(grupoInicial && destinoInicial) && (
                <Card elevation={3} sx={{ mb: 1.5, borderRadius: 2 }}>
                  <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Typography variant="h6" sx={{ mb: 1.5, display: "flex", alignItems: "center", fontSize: "1rem" }}>
                      <LuggageIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: "1.2rem" }} />
                      Pasajeros Paquete
                    </Typography>
                    
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          size="small"
                          fullWidth
                          disablePortal
                          getOptionLabel={(option) => option.label || option.descripcion || ""}
                          isOptionEqualToValue={(option, value) => option.id === value?.id}
                          options={(itemTresLetras || []).map((it) => {
                            const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)
                            const attributeValues = activeAttributes
                              .map((attr, index) => {
                                const attributeValue = it[`itemDatoAtributo${index + 1}`]
                                return attributeValue ? attributeValue : ""
                              })
                              .filter((val) => val.trim() !== "")
                              .join(" ")
                            
                            return {
                              label: `${it.codigo} - ${it.descripcion}${attributeValues ? " - " + attributeValues : ""}`,
                              descripcion: it.descripcion,
                              codigo: it.codigo,
                              id: it.id,
                              atributos: attributeValues,
                            }
                          })}
                          value={selectedGrupo}
                          onChange={(event, newValue) => setSelectedGrupo(newValue)}
                          onInputChange={(event, newInputValue) => setSearchTextGrupo(newInputValue)}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} sx={{ fontSize: "0.875rem", py: 0.5 }}>
                              <Box>
                                <Box sx={{ fontWeight: 500 }}>
                                  {option.descripcion} {option.atributos && option.atributos.trim() !== "" && (
                                    <span style={{ fontWeight: 400, color: "#666" }}>• {option.atributos}</span>
                                  )}
                                </Box>
                                <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                  {option.codigo}
                                </Box>
                              </Box>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Buscar Grupo"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <GroupIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                              }}
                              inputRef={autocompleteGrupoRef}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          size="small"
                          fullWidth
                          disablePortal
                          getOptionLabel={(option) => option.label || option.descripcion || ""}
                          isOptionEqualToValue={(option, value) => option.id === value?.id}
                          options={(selectedGrupo && ubicacionesFiltradas.length > 0
                            ? ubicacionesFiltradas.map((it) => ({
                                id: it.idUbicacion,
                                label: it.ubicacion?.descripcion || ubicaciones.find(u => u.id === it.idUbicacion)?.descripcion || "Sin descripción",
                              }))
                            : ubicaciones.map((it) => ({
                                id: it.id,
                                label: it.descripcion,
                              })))}
                          value={selectedDestino}
                          onChange={(event, newValue) => setSelectedDestino(newValue)}
                          disabled={!selectedGrupo}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Destino"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <LocationOnIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>

                  </CardContent>
                </Card>
              )}

              {/* Tabla de pasajeros */}
              <Card elevation={3} sx={{ borderRadius: 2, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <TableContainer
                  sx={{
                    flex: 1,
                    maxHeight: {
                      xs: "calc(100vh - 400px)",
                      md: "calc(100vh - 350px)",
                    },
                    overflowX: "auto",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>
                          <TableSortLabel
                            active={sortConfig.field === "apellido"}
                            direction={sortConfig.field === "apellido" ? sortConfig.direction : "asc"}
                            onClick={() => handleSort("apellido")}
                          >
                            Pasajeros
                          </TableSortLabel>
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <TableSortLabel
                            active={sortConfig.field === 'dniCuitCuil'}
                            direction={sortConfig.field === 'dniCuitCuil' ? sortConfig.direction : 'asc'}
                            onClick={() => handleSort('dniCuitCuil')}
                          >
                            DNI/CUIT
                          </TableSortLabel>
                        </StyledTableCell>
                        <StyledTableCell align="center">Viaja</StyledTableCell>
                        <StyledTableCell align="center">Liberado</StyledTableCell>
                        <StyledTableCell align="right">
                          <TableSortLabel
                            active={sortConfig.field === 'precio'}
                            direction={sortConfig.field === 'precio' ? sortConfig.direction : 'asc'}
                            onClick={() => handleSort('precio')}
                          >
                            Precio
                          </TableSortLabel>
                        </StyledTableCell>
                        <StyledTableCell align="right">Descuento</StyledTableCell>
                        <StyledTableCell align="right">
                          <TableSortLabel
                            active={sortConfig.field === 'total'}
                            direction={sortConfig.field === 'total' ? sortConfig.direction : 'asc'}
                            onClick={() => handleSort('total')}
                          >
                            Total
                          </TableSortLabel>
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                            Cuotas
                            <Tooltip title="Editar cuotas para todos los pasajeros">
                              <IconButton
                                size="small"
                                onClick={() => handleAbrirPlanCuotas("global")}
                                sx={{
                                  p: 0.3,
                                  color: "white",
                                  "&:hover": {
                                    backgroundColor: alpha(theme.palette.common.white, 0.1),
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell align="center">Acciones</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pasajerosOrdenados.length > 0 ? (
                        pasajerosOrdenados.map((pasajero) => {
                          // ✅ Encontrar el índice real en pasajerosList (necesario para modificaciones)
                          const index = pasajerosList.findIndex(p => 
                            p.idEntidad === pasajero.idEntidad && 
                            p.idTransaccion === pasajero.idTransaccion
                          )                          
                          // ✅ Calcular total dinámicamente: precio base siempre fijo, total según descuentos
                          const precioBase = pasajero.precio || 0
                          let precioConDescuento = precioBase
                          
                          // Si está liberado, el total es 0 (pero el precio base se mantiene)
                          if (pasajero.liberado === true) {
                            precioConDescuento = 0
                          } else {
                            // Aplicar descuento porcentual al precio base
                            const descuentoPorcentual = pasajero.descuento || 0
                            precioConDescuento = precioBase * (1 - descuentoPorcentual / 100)
                          }
                          
                          const rowModificado = pasajeroTieneCambios(pasajero)
                          return (
                            <StyledTableRow 
                              key={`${pasajero.idEntidad}-${pasajero.idTransaccion || index}`}
                              sx={{
                                backgroundColor: pasajero.marcadoParaEliminar
                                  ? alpha(theme.palette.error.light, 0.15)
                                  : rowModificado
                                  ? alpha(theme.palette.warning.light, 0.12)
                                  : (pasajero.esExistente ? alpha(theme.palette.info.light, 0.05) : "inherit"),
                                opacity: pasajero.marcadoParaEliminar ? 0.6 : 1,
                                textDecoration: pasajero.marcadoParaEliminar ? "line-through" : "none"
                              }}
                            >
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {formatPersonaApellidoNombre(pasajero)}
                                  </Typography>
                                  {pasajero.esExistente && (
                                    <Chip 
                                      label="Existente" 
                                      size="small" 
                                      color="info"
                                      sx={{ 
                                        height: 20, 
                                        fontSize: "0.65rem",
                                        fontWeight: "bold"
                                      }}
                                    />
                                  )}
                                  {pasajero.marcadoParaEliminar && (
                                    <Chip 
                                      label="Para eliminar" 
                                      size="small" 
                                      color="error"
                                      sx={{ 
                                        height: 20, 
                                        fontSize: "0.65rem",
                                        fontWeight: "bold"
                                      }}
                                    />
                                  )}
                                  {tieneCuotasPagadas(pasajero) && (
                                    <Tooltip title="Este pasajero ya pago una cuota">
                                      <CheckCircleIcon
                                        fontSize="small"
                                        sx={{ color: "success.main" }}
                                      />
                                    </Tooltip>
                                  )}
                                  {!pasajero.marcadoParaEliminar && rowModificado && (
                                    <Tooltip title={`Cambios: ${difPasajero(pasajero).join(", ")}`}>
                                      <Chip 
                                        label="Modificado" 
                                        size="small" 
                                        color="warning"
                                        sx={{ 
                                          height: 20, 
                                          fontSize: "0.65rem",
                                          fontWeight: "bold"
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {pasajero.dniCuitCuil}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={getViajaEfectivo(pasajero)}
                                  onChange={(e) => {
                                    const updatedList = [...pasajerosList]
                                    updatedList[index] = {
                                      ...updatedList[index],
                                      viaja: e.target.checked
                                    }
                                    setPasajerosList(updatedList.map((p) => normalizarPasajero(p)))
                                  }}
                                  size="small"
                                  sx={{ p: 0.5 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={pasajero.liberado === true}
                                  onChange={(e) => {
                                    const updatedList = [...pasajerosList]
                                    updatedList[index] = {
                                      ...updatedList[index],
                                      liberado: e.target.checked,
                                      // Si está liberado, descuento 100%; si no, 0%
                                      descuento: e.target.checked ? 100 : 0,
                                    }
                                    setPasajerosList(updatedList.map((p) => normalizarPasajero(p)))
                                  }}
                                  size="small"
                                  sx={{ p: 0.5 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ textAlign: "right", display: "block" }}>
                                  {new Intl.NumberFormat("es-AR", {
                                    style: "currency",
                                    currency: "ARS",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  }).format(pasajero.precio)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  value={pasajero.descuento === 0 ? '' : pasajero.descuento}
                                  placeholder="0"
                                  size="small"
                                  onFocus={(e) => e.target.select()}
                                  onChange={(e) => {
                                    const newValue = e.target.value
                                    const updatedList = [...pasajerosList]
                                    updatedList[index] = {
                                      ...updatedList[index],
                                      descuento: newValue === '' ? 0 : Number(newValue)
                                    }
                                    setPasajerosList(updatedList.map((p) => normalizarPasajero(p)))
                                  }}
                                  onBlur={(e) => {
                                    const newValue = e.target.value
                                    handleUpdatePasajeroDescuento(index, newValue === '' ? 0 : newValue)
                                  }}
                                  sx={{
                                    width: 80,
                                    "& .MuiInputBase-root": {
                                      borderRadius: 1,
                                      height: 32,
                                    },
                                  }}
                                  InputProps={{ endAdornment: <span>%</span> }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight="medium" sx={{ textAlign: "right", display: "block" }}>
                                  {new Intl.NumberFormat("es-AR", {
                                    style: "currency",
                                    currency: "ARS",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  }).format(precioConDescuento)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {pasajero.cuotas ?? "-"}
                                  </Typography>
                                  <Tooltip title="Editar cuotas del pasajero">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAbrirPlanCuotas("override", pasajero)}
                                      sx={{ p: 0.4 }}
                                    >
                                      <SettingsIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip
                                  title={
                                    tieneCuotasPagadas(pasajero)
                                      ? "No se puede eliminar ya que el cliente pagó cuotas."
                                      : "Eliminar pasajero"
                                  }
                                >
                                  <IconButton 
                                    color="error" 
                                    size="small" 
                                    disabled={tieneCuotasPagadas(pasajero)}
                                    onClick={() => handleDeletePasajero(index)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </StyledTableRow>
                          )
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                            <Typography variant="body1" color="text.secondary">
                              No hay pasajeros agregados
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {!selectedGrupo || !selectedDestino 
                                ? "Seleccione un grupo y destino para agregar pasajeros"
                                : "Busque y agregue pasajeros para el viaje"
                              }
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    <TableFooter sx={{ position: "sticky", bottom: 0, backgroundColor: "background.paper", zIndex: 10 }}>
                      <TableRow>
                        <TableCell colSpan={8} align="right">
                        </TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
                <Box
                  sx={{
                    p: 1,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Capacidad del viaje:
                    </Typography>
                    <Chip
                      label={`${capacidadPorcentaje.toFixed(1)}%`}
                      size="small"
                      color={capacidadPorcentaje >= 100 ? "error" : "primary"}
                      sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      ({totalEntidadesAgregadas}/{totalButacas || 0})
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteAllPasajeros}
                    disabled={pasajerosList.length === 0}
                    size="small"
                    sx={{ ml: "auto" }}
                  >
                    Eliminar Todos
                  </Button>
                </Box>
              </Card>
            </Grid>

            {/* Right Column - Agregar Pasajeros y Resumen */}
            <Grid item xs={12} md={3}>
              {/* Card de Agregar Pasajeros */}
              <Card elevation={3} sx={{ mb: 1.5, borderRadius: 2 }}>
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography variant="h6" sx={{ mb: 1.5, display: "flex", alignItems: "center", fontSize: "1rem" }}>
                    <DirectionsBusIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: "1.2rem" }} />
                    Agregar Pasajeros
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      disablePortal
                      options={entidadesClientesTresLetras.map((it) => ({
                        label: `${it.descripcion} ${it.apellido} - ${it.dniCuitCuil}`,
                        descripcion: it.descripcion,
                        apellido: it.apellido,
                        dniCuitCuil: it.dniCuitCuil,
                        id: it.id,
                        telefono: it.telefono,
                        email: it.email,
                        direccion: it.direccion,
                        localidad: it.localidad,
                        provincia: it.provincia,
                        idTipoEntidad: it.idTipoEntidad,
                      }))}
                      value={selectedPasajero}
                      onChange={async (event, newValue) => {
                        setSelectedPasajero(newValue)
                        if (newValue) {
                          await handleAddPasajero(newValue)
                        }
                      }}
                      onInputChange={(event, newInputValue) => setSearchTextPasajero(newInputValue)}
                      disabled={!selectedGrupo || !selectedDestino}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Buscar Pasajero"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <SearchIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                          inputRef={autocompletePasajeroRef}
                        />
                      )}
                    />

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="Crear Nuevo Pasajero">
                        <span>
                          <IconButton
                            color="primary"
                            onClick={handleAbrirModalCrearPasajero}
                            disabled={!selectedGrupo || !selectedDestino}
                            sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}` }}
                          >
                            <PersonAddAltIcon />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Button
                        variant="contained"
                        onClick={handleAddPasajero}
                        startIcon={<AddCircleOutlineIcon />}
                        disabled={!selectedPasajero || !selectedGrupo || !selectedDestino}
                        sx={{
                          backgroundColor: "#5b5b5f",
                          color: "white",
                          flex: 1,
                        }}
                      >
                        Agregar
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Resumen del Viaje */}
              <Card elevation={3} sx={{ mb: 1.5, borderRadius: 2 }}>
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography variant="h6" sx={{ mb: 1.5, display: "flex", alignItems: "center", fontSize: "1rem" }}>
                    <ReceiptIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: "1.2rem" }} />
                    Resumen del Viaje
                  </Typography>

                  <Box
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.light, 0.1),
                      p: 2,
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2">Grupo:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedGrupo ? selectedGrupo.descripcion : "-"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2">Destino:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedDestino ? selectedDestino.label : "-"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2">Fecha Viaje:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {fechaInicioViaje || fechaFinViaje
                          ? `${fechaInicioViaje ? dayjs(fechaInicioViaje).format("DD/MM/YYYY") : "-"} - ${fechaFinViaje ? dayjs(fechaFinViaje).format("DD/MM/YYYY") : "-"}`
                          : "-"}
                      </Typography>
                    </Box>
                    
<Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
      Total Butacas:
    </Typography>
    <Chip
      label={totalButacas}
      size="small"
      color="error"
      sx={{
        height: 20,
        fontSize: "0.7rem",
        fontWeight: "bold",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    />
  </Box>
</Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2">Total Pasajeros:</Typography>
                        <Chip 
                          label={totalPasajeros}
                          size="small"
                          color="primary"
                          sx={{ 
                            height: 20, 
                            fontSize: "0.7rem", 
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ color: theme.palette.info.main }}>
                          Total Confirmados:
                        </Typography>
                        <Chip 
                          label={totalConfirmados}
                          size="small"
                          color="info"
                          sx={{ 
                            height: 20, 
                            fontSize: "0.7rem", 
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                      <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
                        • Nuevos:
                      </Typography>
                      <Chip 
                        label={pasajerosList.filter(p => !p.esExistente).length}
                        size="small"
                        color="success"
                        sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }}
                      />
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body1">Subtotal:</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {new Intl.NumberFormat("es-AR", {
                          style: "currency",
                          currency: "ARS",
                        }).format(precioBaseTotal)}
                      </Typography>
                    </Box>

                    {/* Mostrar Total de Liberados si hay pasajeros liberados */}
                    {montoTotalDescuentos > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                          color: 'warning.main'
                        }}
                      >
                        <Typography variant="body2">
                          Total de Liberados ({pasajerosList.filter(p => p.liberado === true).length}):
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          -{new Intl.NumberFormat("es-AR", {
                            style: "currency",
                            currency: "ARS",
                          }).format(montoTotalDescuentos)}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                      <Typography variant="h6">TOTAL:</Typography>
                      <Typography variant="h6" color="rgb(244, 147, 14)" fontWeight="bold">
                        {new Intl.NumberFormat("es-AR", {
                          style: "currency",
                          currency: "ARS",
                        }).format(precioBaseTotal - montoTotalDescuentos)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      fullWidth
                      size="large"
                      disabled={isButtonDisabled || esSoloLectura}
                      startIcon={<SendIcon />}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: "bold",
                        boxShadow: 3,
                        backgroundColor: "#1976d2",
                        color: "white",
                      }}
                    >
                      {pasajerosList.some(p => p.esExistente || p.idTransaccion)
                        ? (cambiosPendientes > 0 ? `Actualizar (${cambiosPendientes})` : "Actualizar")
                        : (cambiosPendientes > 0 ? `Guardar (${cambiosPendientes})` : "Guardar")}
                    </Button>

                    {/* Botón revertir cambios */}
                    <Tooltip title="Revertir cambios">
                      <IconButton
                        size="small"
                        onClick={handleRevertirCambios}
                        disabled={cambiosPendientes === 0 || esSoloLectura}
                        sx={{
                          border: "1px solid #ffb74d",
                          backgroundColor: "#ffb74d",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "#ffa726",
                            borderColor: "#ffa726",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "#e0e0e0",
                            borderColor: "#e0e0e0",
                            color: "#bdbdbd",
                          },
                        }}
                      >
                        <RollbackIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Box>

      {/* Modal para crear pasajero */}
      <CrearEntidadClienteModalComponent
        open={abrirModalCrearPasajero}
        handleClose={handleCerrarModalCrearPasajero}
        entidad={pasajeroAEditar}
        isEditing={isEditing}
        handleAlerta={handleAlerta}
        onClienteCreado={handlePasajeroCreado}
        tipoEntidad="cliente"
        postEntidadOptions={
          selectedGrupo && selectedDestino
            ? {
                validacionCapacidadViaje: {
                  idItem: Number(selectedGrupo.id),
                  idUbicacion: Number(selectedDestino.id),
                  idTipoTransaccion: 7,
                },
              }
            : null
        }
      />

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              p: 3,
              borderRadius: 2,
              bgcolor: "background.paper",
            }}
          >
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">Procesando...</Typography>
          </Box>
        </Box>
      )}

      {/* Modal para gestión de plan de cuotas */}
      <Modal
        open={abrirModalCuotasMasivo}
        onClose={handleCerrarPlanCuotas}
        aria-labelledby="modal-cuotas-plan"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "98%", sm: 960, md: 1120 },
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {/* Encabezado fijo */}
          <Box 
            sx={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center", 
              p: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
              bgcolor: "background.paper",
              zIndex: 1
            }}
          >
            <Typography id="modal-cuotas-plan" variant="subtitle1" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
              {planCuotasState.mode === "override"
                ? `Cuotas personalizadas - ${planCuotasState.targetPasajero?.nombre || "Pasajero"}`
                : "Plan de cuotas del viaje"}
            </Typography>
            <IconButton size="small" onClick={handleCerrarPlanCuotas} sx={{ p: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Contenido con scroll */}
          <Box sx={{ flex: 1, overflow: "auto", p: 1.5 }}>

          {planCuotasState.loading ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {planError && (
                  <Alert severity="error" sx={{ py: 0.5, fontSize: "0.75rem" }}>{planError}</Alert>
                )}

                {planCuotasState.mode === "override" && planCuotasState.targetPasajero && (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                      <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                        Pasajero: <strong>{planCuotasState.targetPasajero.nombre} {planCuotasState.targetPasajero.apellido}</strong>
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                        Monto neto: <strong style={{ fontSize: "0.85rem" }}>${formatNumberArgentina(calcularPrecioNetoPasajero(planCuotasState.targetPasajero))}</strong>
                      </Typography>
                    </Box>
                  </Alert>
                )}

                {/* ⭐ NUEVO: Campos de configuración en modo override */}
                {planCuotasState.mode === "override" && planCuotasState.targetPasajero && (
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Cantidad de cuotas"
                        type="number"
                        value={overrideConfig.cantidadCuotas || 0}
                        onChange={(e) => setOverrideConfig(prev => ({ ...prev, cantidadCuotas: Number(e.target.value) }))}
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="% Interés"
                        type="number"
                        value={overrideConfig.porcentajeInteres || 0}
                        onChange={(e) => setOverrideConfig(prev => ({ ...prev, porcentajeInteres: Number(e.target.value) }))}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="% Descuento"
                        type="number"
                        value={overrideConfig.porcentajeDescuento || 0}
                        onChange={(e) => setOverrideConfig(prev => ({ ...prev, porcentajeDescuento: Number(e.target.value) }))}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={handleRecalcularPlanPasajero}
                        disabled={planGuardando}
                        startIcon={<CalculateIcon fontSize="small" />}
                        sx={{ fontSize: "0.75rem", height: "40px" }}
                      >
                        Recalcular plan
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!!overrideConfig.interesPrimeraUltima}
                            onChange={(e) => setOverrideConfig(prev => ({ ...prev, interesPrimeraUltima: e.target.checked }))}
                          />
                        }
                        label={<Typography variant="caption" sx={{ fontSize: "0.75rem" }}>Aplicar interés en primera y última cuota</Typography>}
                      />
                    </Grid>
                  </Grid>
                )}

                {planCuotasState.header && planCuotasState.mode === "global" && (
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Precio (precio por pasajero)"
                        type="text"
                        value={formatNumberArgentina(planCuotasState.header.precioBaseReferencia || 0)}
                        onChange={(e) => {
                          const parsed = parseNumberArgentina(e.target.value)
                          handlePlanHeaderChange("precioBaseReferencia", parsed)
                        }}
                        InputProps={{ 
                          startAdornment: <InputAdornment position="start" sx={{ fontSize: "0.85rem", fontWeight: 500 }}>$</InputAdornment>
                        }}
                        sx={{
                          "& .MuiInputBase-root": { fontSize: "0.9rem", fontWeight: 500 },
                          "& .MuiInputBase-input": { fontWeight: 500 }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Cantidad de cuotas"
                        type="number"
                        value={planCuotasState.header.cantidadCuotas || 0}
                        onChange={(e) => handlePlanHeaderChange("cantidadCuotas", e.target.value)}
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="% Interés"
                        type="number"
                        value={planCuotasState.header.porcentajeInteres || 0}
                        onChange={(e) => handlePlanHeaderChange("porcentajeInteres", e.target.value)}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="% Descuento"
                        type="number"
                        value={planCuotasState.header.porcentajeDescuento || 0}
                        onChange={(e) => handlePlanHeaderChange("porcentajeDescuento", e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!!planCuotasState.header.interesPrimeraUltima}
                            onChange={(e) => handlePlanHeaderChange("interesPrimeraUltima", e.target.checked)}
                          />
                        }
                        label={<Typography variant="caption" sx={{ fontSize: "0.75rem" }}>Aplicar interés en primera y última cuota</Typography>}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleRecalcularPlan}
                        disabled={planGuardando}
                        startIcon={<CalculateIcon fontSize="small" />}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Recalcular plan automáticamente
                      </Button>
                    </Grid>
                  </Grid>
                )}

                <TableContainer sx={{ maxHeight: "45vh" }}>
                  <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { padding: "6px 8px", fontSize: "0.75rem" } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: "0.7rem", fontWeight: 600, py: 0.5 }}>Cuota</TableCell>
                        <TableCell sx={{ fontSize: "0.7rem", fontWeight: 600, py: 0.5 }}>1er Vto</TableCell>
                        <TableCell sx={{ fontSize: "0.7rem", fontWeight: 600, py: 0.5 }}>2do Vto</TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.7rem", fontWeight: 600, py: 0.5 }}>Monto</TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.7rem", fontWeight: 600, py: 0.5 }}>Descuento</TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.7rem", fontWeight: 600, py: 0.5 }}>Interés</TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.7rem", fontWeight: 600, py: 0.5 }}>Total cuota</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {planCuotasState.detalles.map((detalle, index) => (
                        <TableRow key={detalle.cuotaNro} hover sx={{ "& .MuiTableCell-root": { py: 0.5 } }}>
                          <TableCell sx={{ fontSize: "0.75rem" }}>{detalle.cuotaNro}</TableCell>
                          <TableCell>
                            <DatePicker
                              value={detalle.fechaPrimerVto ? dayjs(detalle.fechaPrimerVto) : null}
                              onChange={(date) => handlePlanDetalleChange(index, "fechaPrimerVto", date)}
                              format="DD/MM/YYYY"
                              slotProps={{ 
                                textField: { 
                                  size: "small", 
                                  sx: { 
                                    width: 110,
                                    "& .MuiInputBase-root": { fontSize: "0.75rem", height: "32px" },
                                    "& .MuiInputBase-input": { padding: "6px 8px" }
                                  } 
                                } 
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <DatePicker
                              value={detalle.fechaSegundoVto ? dayjs(detalle.fechaSegundoVto) : null}
                              onChange={(date) => handlePlanDetalleChange(index, "fechaSegundoVto", date)}
                              format="DD/MM/YYYY"
                              slotProps={{ 
                                textField: { 
                                  size: "small", 
                                  sx: { 
                                    width: 110,
                                    "& .MuiInputBase-root": { fontSize: "0.75rem", height: "32px" },
                                    "& .MuiInputBase-input": { padding: "6px 8px" }
                                  } 
                                } 
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="text"
                              size="small"
                              value={formatNumberArgentina(detalle.montoBase ?? 0)}
                              onChange={(e) => {
                                const parsed = parseNumberArgentina(e.target.value)
                                handlePlanDetalleChange(index, "montoBase", parsed)
                              }}
                              sx={{ 
                                width: 120,
                                "& .MuiInputBase-root": { fontSize: "0.9rem", height: "36px", fontWeight: 500 },
                                "& .MuiInputBase-input": { padding: "6px 8px", textAlign: "right", fontWeight: 500 }
                              }}
                              InputProps={{ 
                                startAdornment: <InputAdornment position="start" sx={{ fontSize: "0.85rem", fontWeight: 500 }}>$</InputAdornment> 
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="text"
                              size="small"
                              value={formatNumberArgentina(detalle.montoDescuento ?? 0)}
                              onChange={(e) => {
                                const parsed = parseNumberArgentina(e.target.value)
                                handlePlanDetalleChange(index, "montoDescuento", parsed)
                              }}
                              sx={{ 
                                width: 120,
                                "& .MuiInputBase-root": { fontSize: "0.9rem", height: "36px", fontWeight: 500 },
                                "& .MuiInputBase-input": { padding: "6px 8px", textAlign: "right", fontWeight: 500 }
                              }}
                              InputProps={{ 
                                startAdornment: <InputAdornment position="start" sx={{ fontSize: "0.85rem", fontWeight: 500 }}>$</InputAdornment> 
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="text"
                              size="small"
                              value={formatNumberArgentina(detalle.montoInteres ?? 0)}
                              onChange={(e) => {
                                const parsed = parseNumberArgentina(e.target.value)
                                handlePlanDetalleChange(index, "montoInteres", parsed)
                              }}
                              sx={{ 
                                width: 120,
                                "& .MuiInputBase-root": { fontSize: "0.9rem", height: "36px", fontWeight: 500 },
                                "& .MuiInputBase-input": { padding: "6px 8px", textAlign: "right", fontWeight: 500 }
                              }}
                              InputProps={{ 
                                startAdornment: <InputAdornment position="start" sx={{ fontSize: "0.85rem", fontWeight: 500 }}>$</InputAdornment> 
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="text"
                              size="small"
                              value={formatNumberArgentina(detalle.montoTotal ?? 0)}
                              onChange={(e) => {
                                const parsed = parseNumberArgentina(e.target.value)
                                handlePlanDetalleChange(index, "montoTotal", parsed)
                              }}
                              sx={{ 
                                width: 120,
                                "& .MuiInputBase-root": { fontSize: "0.9rem", height: "36px", fontWeight: 500 },
                                "& .MuiInputBase-input": { padding: "6px 8px", textAlign: "right", fontWeight: 500 }
                              }}
                              InputProps={{ 
                                startAdornment: <InputAdornment position="start" sx={{ fontSize: "0.85rem", fontWeight: 500 }}>$</InputAdornment> 
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </LocalizationProvider>
          )}
          </Box>

          {/* Pie de página fijo */}
          <Box 
            sx={{ 
              borderTop: "1px solid",
              borderColor: "divider",
              p: 1.5,
              flexShrink: 0,
              bgcolor: "background.paper",
              zIndex: 1
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Typography variant="body1" sx={{ fontSize: "1rem", fontWeight: 500, color: "error.main" }}>
                  Total Descuento: <strong style={{ fontSize: "1.1rem" }}>-${formatNumberArgentina(planTotales.descuento)}</strong>
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1rem", fontWeight: 500 }}>
                  Total Interés: <strong style={{ fontSize: "1.1rem" }}>${formatNumberArgentina(planTotales.interes)}</strong>
                </Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ fontSize: "1.05rem" }}>
                  Total: <strong style={{ fontSize: "1.2rem" }}>${formatNumberArgentina(planTotales.total)}</strong>
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={handleCerrarPlanCuotas} 
                  disabled={planGuardando}
                  sx={{ fontSize: "0.75rem", minWidth: "auto", px: 1.5 }}
                >
                  Cerrar
                </Button>
                {planCuotasState.mode === "global" ? (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleGuardarPlan}
                      disabled={planGuardando}
                      startIcon={<SaveIcon fontSize="small" />}
                      sx={{ fontSize: "0.75rem", minWidth: "auto", px: 1 }}
                    >
                      Guardar plan
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAplicarPlanGlobal}
                      disabled={planGuardando || planCuotasState.detalles.length === 0}
                      startIcon={<CheckCircleIcon fontSize="small" />}
                      sx={{ fontSize: "0.75rem", minWidth: "auto", px: 1 }}
                    >
                      Aplicar a pasajeros
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleGuardarOverridePasajero}
                      disabled={planGuardando || planCuotasState.detalles.length === 0}
                      startIcon={<SaveIcon fontSize="small" />}
                      sx={{ fontSize: "0.75rem", minWidth: "auto", px: 1 }}
                    >
                      Guardar personalizado
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
})

export default GestionGenerarVentaComponent
